import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  CheckCircleOutline as ApproveIcon,
  CancelOutlined as RejectIcon,
  RefreshOutlined as RefreshIcon,
  TrendingUp as TrendUpIcon,
  TrendingDown as TrendDownIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { fetchClients } from '@src/api/actions/clients';
import { fetchClientTerritories } from '@src/api/actions/clientTerritories';
import { fetchVerticalsForClient } from '@src/api/actions/verticals';
import {
  fetchOptimizerRuns,
  fetchOptimizerRunDetail,
  fetchOptimizerRunChanges,
  approveOptimizerRun,
  rejectOptimizerRun,
  fetchOptimizerConfigs,
  createOptimizerConfig,
  updateOptimizerConfig,
  fetchOptimizerGlobalConfig,
  updateOptimizerGlobalConfig,
  triggerOptimizerRun,
  fetchGeoAuditsLatest,
  fetchGeoAuditHistory,
  triggerGeoAudit,
  fetchPacingHolds,
} from '@src/api/actions';

// ── Formatters ──────────────────────────────────────────────────

const fmtUSD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const fmtCents = (cents) => fmtUSD.format((cents || 0) / 100);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-');
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-');

// BudgetChangeLog.previous_value / new_value are JSONField dicts, not strings.
// Render them as readable text so they don't blow up React with "Objects are not
// valid as a React child" inside the failed-changes table.
const fmtChangeValue = (v) => {
  if (v == null) return '—';
  if (typeof v !== 'object') return String(v);
  if (Array.isArray(v)) return v.length ? JSON.stringify(v) : '—';
  const keys = Object.keys(v);
  if (!keys.length) return '—';
  if ('daily_budget_cents' in v) return fmtCents(v.daily_budget_cents);
  if ('city_count' in v) {
    const adj = Array.isArray(v.bid_adjustments) ? `${v.bid_adjustments.length} buckets` : null;
    return [adj, `${v.city_count} cities`].filter(Boolean).join(', ');
  }
  return keys.map((k) => `${k}: ${typeof v[k] === 'object' ? JSON.stringify(v[k]) : v[k]}`).join(', ');
};

// Runs are created daily per client, so the table defaults to a tight recent
// window (today + yesterday) to keep the payload small. Widen via the From/To
// date pickers. Bump this one constant to change the default span.
const DEFAULT_LOOKBACK_DAYS = 2;
const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const tzMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzMs).toISOString().slice(0, 10);
};

// Secondary columns collapse away on phones so the runs table's key columns
// (Client / Date / Delta / Status / Actions) stay readable. The hidden values
// reappear as a chip row when a run is expanded — see the mobile-only block.
const HIDE_ON_MOBILE_SX = { display: { xs: 'none', md: 'table-cell' } };

// ── Stat Card ───────────────────────────────────────────────────

function StatCard({ label, value, sub, color, onClick, active }) {
  const clickable = typeof onClick === 'function';
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 2,
        flex: 1,
        minWidth: 140,
        cursor: clickable ? 'pointer' : 'default',
        borderColor: active ? (color || 'primary.main') : 'divider',
        borderWidth: active ? 2 : 1,
        bgcolor: active ? 'action.selected' : 'background.paper',
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        '&:hover': clickable ? { borderColor: color || 'primary.main', bgcolor: 'action.hover' } : undefined,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ mt: 0.5, color: color || 'text.primary' }}>{value}</Typography>
      {sub && <Typography variant="body2" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

// ── Status Chip ─────────────────────────────────────────────────

function StatusChip({ status, type = 'approval' }) {
  const colorMap = type === 'approval'
    ? { draft: 'warning', approved: 'success', auto_approved: 'success', rejected: 'error' }
    : { not_applied: 'default', applying: 'info', applied: 'success', partial: 'warning', failed: 'error' };
  const labelMap = type === 'approval'
    ? { draft: 'Pending', approved: 'Approved', auto_approved: 'Auto', rejected: 'Rejected' }
    : { not_applied: 'Not Applied', applying: 'Applying…', applied: 'Applied', partial: 'Partial', failed: 'Failed' };
  const isApplying = type === 'apply' && status === 'applying';
  return (
    <Chip
      label={labelMap[status] || status}
      color={colorMap[status] || 'default'}
      size="small"
      variant={type === 'apply' ? 'outlined' : 'filled'}
      icon={isApplying ? <CircularProgress size={12} sx={{ ml: 0.5 }} /> : undefined}
    />
  );
}

// ── Optimizer Runs Tab ──────────────────────────────────────────

function OptimizerRunsTab({ clients, initialClientId = '', initialRunId = '' }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterClientId, setFilterClientId] = useState(initialClientId ? String(initialClientId) : '');
  const [filterStatus, setFilterStatus] = useState('');
  // Client-side "needs attention" lens over the loaded rows: '' | 'pending' | 'errors'.
  // Driven by the stat cards / attention band; instant, no refetch.
  const [quickView, setQuickView] = useState('');
  // Deep-linking to a specific run? Drop the default lookback so the run loads
  // no matter how old it is — otherwise an alert from last week opens an empty
  // table.
  const [filterDateAfter, setFilterDateAfter] = useState(
    () => (initialRunId ? '' : isoDaysAgo(DEFAULT_LOOKBACK_DAYS - 1)),
  );
  const [filterDateBefore, setFilterDateBefore] = useState('');
  const [expandedId, setExpandedId] = useState(initialRunId ? Number(initialRunId) : null);
  const [expandedVrIds, setExpandedVrIds] = useState({});
  const [approveRun, setApproveRun] = useState(null);
  const [rejectRun, setRejectRun] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [blockingAudit, setBlockingAudit] = useState(null);
  const [overrideAck, setOverrideAck] = useState(false);
  const [triggerClientId, setTriggerClientId] = useState('');
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [triggerLog, setTriggerLog] = useState(null);
  const [selectedRunIds, setSelectedRunIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [auditByClient, setAuditByClient] = useState({});
  // Cached failed-change-log details per run, fetched lazily when a run with
  // apply errors is expanded. Shape: { [runId]: { loading, items, error } }
  const [failedChangesByRun, setFailedChangesByRun] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {};
    if (filterClientId) params.client_id = filterClientId;
    if (filterStatus) params.approval_status = filterStatus;
    if (filterDateAfter) params.date_after = filterDateAfter;
    if (filterDateBefore) params.date_before = filterDateBefore;
    fetchOptimizerRuns(params)
      .then((res) => setRuns(res.data?.results || res.data || []))
      .catch((err) => setError(err?.message || 'Failed to load optimizer runs.'))
      .finally(() => setLoading(false));
    // Fire audit fetch in parallel — surfaces a per-row red badge so users see
    // which runs will hit the apply gate before clicking Approve.
    fetchGeoAuditsLatest()
      .then((res) => {
        const map = {};
        (res.data || []).forEach((a) => { map[a.client] = a; });
        setAuditByClient(map);
      })
      .catch(() => setAuditByClient({}));
  }, [filterClientId, filterStatus, filterDateAfter, filterDateBefore]);

  useEffect(() => { load(); }, [load]);

  // Poll only the rows that are mid-apply. We fetch each applying run's detail
  // and merge it into state in place, so non-applying rows keep their object
  // identity and React doesn't re-render the whole table every 3s.
  useEffect(() => {
    const applyingIds = runs.filter((r) => r.apply_status === 'applying').map((r) => r.id);
    if (!applyingIds.length) return undefined;
    const intervalId = setInterval(() => {
      Promise.all(applyingIds.map((rid) => fetchOptimizerRunDetail(rid).catch(() => null)))
        .then((responses) => {
          setRuns((prev) => prev.map((r) => {
            const match = responses.find((resp) => resp?.data?.id === r.id);
            if (!match) return r;
            // Only swap the row if apply_status actually changed — otherwise keep
            // the original reference so memoized children don't re-render.
            if (match.data.apply_status === r.apply_status && match.data.apply_error === r.apply_error) {
              return r;
            }
            return { ...r, ...match.data };
          }));
        });
    }, 3000);
    return () => clearInterval(intervalId);
  }, [runs]);

  // When the user expands a row that had apply errors, fetch the per-change
  // log entries so we can surface the actual error_message per failed write
  // (not just the aggregated count from apply_error).
  useEffect(() => {
    if (!expandedId) return;
    const run = runs.find((r) => r.id === expandedId);
    if (!run) return;
    if (run.apply_status !== 'partial' && run.apply_status !== 'failed') return;
    if (failedChangesByRun[expandedId]) return;  // already fetched/fetching
    setFailedChangesByRun((prev) => ({ ...prev, [expandedId]: { loading: true, items: [], error: '' } }));
    fetchOptimizerRunChanges(expandedId)
      .then((res) => {
        const all = res.data || [];
        const failed = all.filter((c) => c.success === false && c.error_message);
        setFailedChangesByRun((prev) => ({ ...prev, [expandedId]: { loading: false, items: failed, error: '' } }));
      })
      .catch((err) => {
        setFailedChangesByRun((prev) => ({
          ...prev,
          [expandedId]: { loading: false, items: [], error: err?.message || 'Failed to load change details.' },
        }));
      });
  }, [expandedId, runs, failedChangesByRun]);

  const handleApprove = (override = false) => {
    if (!approveRun) return;
    setActionLoading(true);
    setActionError('');
    approveOptimizerRun(approveRun.id, { override })
      .then(() => { setApproveRun(null); setBlockingAudit(null); load(); })
      .catch((err) => {
        const data = err?.response?.data;
        if (data?.audit) {
          setBlockingAudit(data.audit);
          setActionError(data.detail || 'Geo data audit is RED for this client.');
        } else {
          setActionError(data?.detail || err?.message || 'Approval failed.');
        }
      })
      .finally(() => setActionLoading(false));
  };

  const handleReject = () => {
    if (!rejectRun) return;
    setActionLoading(true);
    setActionError('');
    rejectOptimizerRun(rejectRun.id, rejectNotes)
      .then(() => { setRejectRun(null); setRejectNotes(''); load(); })
      .catch((err) => setActionError(err?.response?.data?.detail || err?.message || 'Rejection failed.'))
      .finally(() => setActionLoading(false));
  };

  const draftRuns = useMemo(() => runs.filter((r) => r.approval_status === 'draft'), [runs]);
  const allDraftSelected = draftRuns.length > 0 && draftRuns.every((r) => selectedRunIds.includes(r.id));
  const someDraftSelected = selectedRunIds.length > 0;

  const toggleSelectRun = (runId) => {
    setSelectedRunIds((prev) => prev.includes(runId) ? prev.filter((id) => id !== runId) : [...prev, runId]);
  };

  const toggleSelectAll = () => {
    if (allDraftSelected) {
      setSelectedRunIds([]);
    } else {
      setSelectedRunIds(draftRuns.map((r) => r.id));
    }
  };

  const handleBulkApprove = async () => {
    setBulkLoading(true);
    setActionError('');
    const skipped = [];
    try {
      for (const id of selectedRunIds) {
        try {
          await approveOptimizerRun(id);
        } catch (err) {
          const data = err?.response?.data;
          // Bulk approve never overrides red audits — surface the skip and move on.
          if (data?.audit) {
            skipped.push(`Run ${id}: ${data.detail || 'red audit'}`);
            continue;
          }
          throw err;
        }
      }
      setSelectedRunIds([]);
      load();
      if (skipped.length) {
        setActionError(`${skipped.length} run(s) skipped due to red audit. Open each individually to override:\n${skipped.join('\n')}`);
      }
    } catch (err) {
      setActionError(err?.response?.data?.detail || err?.message || 'Bulk approval failed.');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkReject = async () => {
    setBulkLoading(true);
    setActionError('');
    try {
      for (const id of selectedRunIds) {
        await rejectOptimizerRun(id);
      }
      setSelectedRunIds([]);
      load();
    } catch (err) {
      setActionError(err?.response?.data?.detail || err?.message || 'Bulk rejection failed.');
    } finally {
      setBulkLoading(false);
    }
  };

  // Summary stats
  const stats = useMemo(() => {
    const pending = runs.filter((r) => r.approval_status === 'draft').length;
    const applied = runs.filter((r) => r.apply_status === 'applied').length;
    const errors = runs.filter((r) => r.apply_status === 'partial' || r.apply_status === 'failed').length;
    return { pending, applied, errors, total: runs.length };
  }, [runs]);

  // Clients in the current view sitting on a RED geo audit — they'll hit the
  // apply gate, so they belong in the attention band even if nothing else is wrong.
  const redAuditCount = useMemo(() => {
    const ids = new Set();
    runs.forEach((r) => {
      if (auditByClient[r.client]?.overall_status === 'red') ids.add(r.client);
    });
    return ids.size;
  }, [runs, auditByClient]);

  const needsAttention = stats.pending + stats.errors + redAuditCount > 0;

  // Rows actually rendered, after the client-side quick-view lens.
  const visibleRuns = useMemo(() => {
    if (quickView === 'pending') return runs.filter((r) => r.approval_status === 'draft');
    if (quickView === 'errors') return runs.filter((r) => r.apply_status === 'partial' || r.apply_status === 'failed');
    return runs;
  }, [runs, quickView]);

  // Stat-card / band click → set the lens and clear the server-side status
  // dropdown so the two filters never silently fight each other.
  const applyQuickView = (view) => {
    setQuickView((prev) => (prev === view ? '' : view));
    setFilterStatus('');
  };

  return (
    <>
      {/* Summary cards — Pending / Errors double as one-click filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <StatCard
          label="Total Runs"
          value={stats.total}
          onClick={quickView ? () => setQuickView('') : undefined}
          active={!quickView}
        />
        <StatCard
          label="Pending Approval"
          value={stats.pending}
          color={stats.pending > 0 ? 'warning.main' : undefined}
          onClick={() => applyQuickView('pending')}
          active={quickView === 'pending'}
        />
        <StatCard label="Applied" value={stats.applied} color="success.main" />
        <StatCard
          label="Apply Errors"
          value={stats.errors}
          color={stats.errors > 0 ? 'error.main' : undefined}
          onClick={() => applyQuickView('errors')}
          active={quickView === 'errors'}
        />
      </Stack>

      {/* Needs-attention band — the daily exception queue. Most clients auto-apply,
          so this surfaces only the runs that actually need a human. */}
      {needsAttention && (
        <Alert
          severity={stats.errors > 0 || redAuditCount > 0 ? 'warning' : 'info'}
          icon={false}
          sx={{ '& .MuiAlert-message': { width: '100%' } }}
        >
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography variant="body2" fontWeight={600}>Needs attention:</Typography>
            {stats.pending > 0 && (
              <Chip
                label={`${stats.pending} pending approval`}
                size="small"
                color="warning"
                variant={quickView === 'pending' ? 'filled' : 'outlined'}
                onClick={() => applyQuickView('pending')}
              />
            )}
            {stats.errors > 0 && (
              <Chip
                label={`${stats.errors} apply error${stats.errors > 1 ? 's' : ''}`}
                size="small"
                color="error"
                variant={quickView === 'errors' ? 'filled' : 'outlined'}
                onClick={() => applyQuickView('errors')}
              />
            )}
            {redAuditCount > 0 && (
              <Tooltip title="These clients have a RED geo-data audit; approving requires an override.">
                <Chip label={`${redAuditCount} red audit${redAuditCount > 1 ? 's' : ''}`} size="small" color="error" variant="outlined" />
              </Tooltip>
            )}
            {quickView && (
              <Button size="small" sx={{ ml: 'auto' }} onClick={() => setQuickView('')}>
                Show all ({runs.length})
              </Button>
            )}
          </Stack>
        </Alert>
      )}

      {/* Filters + Table */}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {actionError && <Alert severity="error">{actionError}</Alert>}

            {someDraftSelected && (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 1.5, bgcolor: 'action.selected', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={600}>{selectedRunIds.length} selected</Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  disabled={bulkLoading}
                  onClick={handleBulkApprove}
                >
                  {bulkLoading ? <CircularProgress size={18} /> : 'Approve All'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<RejectIcon />}
                  disabled={bulkLoading}
                  onClick={handleBulkReject}
                >
                  Reject All
                </Button>
                <Button size="small" onClick={() => setSelectedRunIds([])}>Clear</Button>
              </Stack>
            )}

            {/* On phones this collapses to a single full-width column so the trigger
                controls (Run for Client + Dry Run / Run & Apply) stay reachable instead
                of overflowing off-screen to the right. On md+ it's a wrapping row. */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
              flexWrap="wrap"
              useFlexGap
            >
              <Autocomplete
                options={clients}
                getOptionLabel={(opt) => opt.name || ''}
                value={clients.find((c) => c.id === Number(filterClientId)) || null}
                onChange={(_, val) => setFilterClientId(val?.id || '')}
                sx={{ minWidth: 250, width: { xs: '100%', md: 250 } }}
                renderInput={(params) => <TextField {...params} label="Filter by Client" size="small" />}
              />
              <FormControl sx={{ minWidth: 150, width: { xs: '100%', md: 150 } }}>
                <InputLabel size="small">Status</InputLabel>
                <Select value={filterStatus} label="Status" onChange={(e) => { setFilterStatus(e.target.value); setQuickView(''); }} size="small">
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="draft">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="auto_approved">Auto-Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="From"
                type="date"
                size="small"
                value={filterDateAfter}
                onChange={(e) => setFilterDateAfter(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', md: 160 } }}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                value={filterDateBefore}
                onChange={(e) => setFilterDateBefore(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', md: 160 } }}
              />
              <Button startIcon={<RefreshIcon />} variant="outlined" size="small" onClick={load} disabled={loading}>
                Refresh
              </Button>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
              <Autocomplete
                options={clients}
                getOptionLabel={(opt) => opt.name || ''}
                value={clients.find((c) => c.id === Number(triggerClientId)) || null}
                onChange={(_, val) => setTriggerClientId(val?.id || '')}
                sx={{ minWidth: 200, width: { xs: '100%', md: 200 } }}
                renderInput={(params) => <TextField {...params} label="Run for Client" size="small" />}
              />
              <Button
                variant="outlined"
                size="small"
                disabled={!triggerClientId || triggerLoading}
                onClick={() => {
                  setTriggerLoading(true);
                  setTriggerLog(null);
                  setActionError('');
                  triggerOptimizerRun(triggerClientId, true)
                    .then((res) => {
                      setTriggerLog(res.data?.log || []);
                      const newRun = res.data?.run;
                      if (newRun) {
                        setRuns((prev) => [newRun, ...prev]);
                      } else {
                        load();
                      }
                    })
                    .catch((err) => {
                      const data = err?.response?.data;
                      setTriggerLog(data?.log || []);
                      setActionError(data?.detail || err?.message || 'Run failed.');
                    })
                    .finally(() => setTriggerLoading(false));
                }}
              >
                {triggerLoading ? <CircularProgress size={18} /> : 'Dry Run'}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="warning"
                disabled={!triggerClientId || triggerLoading}
                onClick={() => {
                  if (!window.confirm('This will apply changes to Facebook immediately. Are you sure?')) return;
                  setTriggerLoading(true);
                  setTriggerLog(null);
                  setActionError('');
                  triggerOptimizerRun(triggerClientId, false)
                    .then((res) => {
                      setTriggerLog(res.data?.log || []);
                      const newRun = res.data?.run;
                      if (newRun) {
                        setRuns((prev) => [newRun, ...prev]);
                      } else {
                        load();
                      }
                    })
                    .catch((err) => {
                      const data = err?.response?.data;
                      setTriggerLog(data?.log || []);
                      setActionError(data?.detail || err?.message || 'Run failed.');
                    })
                    .finally(() => setTriggerLoading(false));
                }}
              >
                {triggerLoading ? <CircularProgress size={18} /> : 'Run & Apply'}
              </Button>
            </Stack>

            {/* Step-by-step log panel */}
            {triggerLog && (
              <Card variant="outlined" sx={{ bgcolor: 'grey.900', maxHeight: 500, overflow: 'auto' }}>
                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                  <Typography variant="caption" sx={{ color: 'grey.400', mb: 1, display: 'block' }}>
                    Optimizer Run Log ({triggerLog.length} steps)
                    <Button size="small" sx={{ ml: 2, color: 'grey.500' }} onClick={() => setTriggerLog(null)}>
                      Close
                    </Button>
                  </Typography>
                  {triggerLog.map((entry, i) => {
                    const stepColors = {
                      start: '#64b5f6', complete: '#81c784', campaign_enriched: '#ce93d8',
                      budget_change: '#ffb74d', value_rule: '#4fc3f7', pause_flag: '#ef5350',
                      client_funnel: '#a5d6a7', deal_stability: '#fff176', whatif: '#90caf9',
                      campaigns_loaded: '#80cbc4', pause_flags: '#e0e0e0', no_campaigns: '#ef9a9a',
                    };
                    const color = stepColors[entry.step] || '#e0e0e0';
                    return (
                      <Box key={i} sx={{ py: 0.3, fontFamily: 'monospace', fontSize: 12 }}>
                        <Typography component="span" sx={{ color, fontWeight: 600, fontSize: 12, fontFamily: 'monospace' }}>
                          [{entry.step}]
                        </Typography>
                        <Typography component="span" sx={{ color: '#e0e0e0', ml: 1, fontSize: 12, fontFamily: 'monospace' }}>
                          {entry.detail}
                        </Typography>
                        {entry.data && (
                          <Box sx={{ ml: 3, mt: 0.3 }}>
                            {typeof entry.data === 'object' && !Array.isArray(entry.data) ? (
                              Object.entries(entry.data).map(([k, v]) => {
                                if (k === 'campaigns' || k === 'why_drivers') return null;
                                const display = typeof v === 'object' ? JSON.stringify(v) : String(v ?? '');
                                if (!display || display === 'null' || display === '{}') return null;
                                return (
                                  <Typography key={k} sx={{ color: '#9e9e9e', fontSize: 11, fontFamily: 'monospace' }}>
                                    {k}: {display}
                                  </Typography>
                                );
                              })
                            ) : (
                              <Typography sx={{ color: '#9e9e9e', fontSize: 11, fontFamily: 'monospace' }}>
                                {JSON.stringify(entry.data)}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : runs.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No optimizer runs found. Runs are created daily when clients have optimizer configs enabled.</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    // Fixed layout with allocated column widths so the table always
                    // fits one view regardless of filter. (Under `auto` layout the
                    // long no-wrap apply-error sub-rows blew the table width out and
                    // forced horizontal scroll when no filter was applied; Client
                    // takes the leftover space.)
                    tableLayout: 'fixed',
                    width: '100%',
                    '& .MuiTableCell-root': { px: { xs: 0.5, md: 1 } },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 40, p: 0.5 }}>
                        <Checkbox
                          size="small"
                          checked={allDraftSelected}
                          indeterminate={someDraftSelected && !allDraftSelected}
                          onChange={toggleSelectAll}
                          disabled={draftRuns.length === 0}
                        />
                      </TableCell>
                      <TableCell sx={{ width: 40, p: 0.5 }} />
                      <TableCell>Client</TableCell>
                      <TableCell sx={{ width: 140 }}>Date</TableCell>
                      <TableCell align="center" sx={{ ...HIDE_ON_MOBILE_SX, width: 75 }}>Budgets</TableCell>
                      <TableCell align="center" sx={{ ...HIDE_ON_MOBILE_SX, width: 65 }}>Rules</TableCell>
                      <TableCell align="right" sx={{ ...HIDE_ON_MOBILE_SX, width: 95 }}>Current</TableCell>
                      <TableCell align="right" sx={{ ...HIDE_ON_MOBILE_SX, width: 115 }}>Recommended</TableCell>
                      <TableCell align="right" sx={{ width: 105 }}>Delta</TableCell>
                      <TableCell align="center" sx={{ width: 95 }}>Status</TableCell>
                      <TableCell align="center" sx={{ width: 90 }}>Applied</TableCell>
                      <TableCell align="right" sx={{ width: 84 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRuns.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={12} sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                          {quickView === 'pending'
                            ? 'No runs pending approval in this view.'
                            : quickView === 'errors'
                              ? 'No apply errors in this view.'
                              : 'No runs match the current filters.'}
                        </TableCell>
                      </TableRow>
                    )}
                    {visibleRuns.map((run) => {
                      const s = run.summary_snapshot || {};
                      const isOpen = expandedId === run.id;
                      const snapshots = run.campaign_budget_snapshot || [];
                      const currentCents = s.current_daily_budget_total_cents || 0;
                      const recCents = s.recommended_daily_budget_total_cents || 0;
                      const deltaCents = recCents - currentCents;
                      const rulesOnly = Boolean(s.rules_only);

                      return (
                        <Fragment key={run.id}>
                          <TableRow hover sx={{ '& > *': { borderBottom: isOpen ? 'none' : undefined } }}>
                            <TableCell sx={{ p: 0.5 }}>
                              {run.approval_status === 'draft' && (
                                <Checkbox
                                  size="small"
                                  checked={selectedRunIds.includes(run.id)}
                                  onChange={() => toggleSelectRun(run.id)}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => setExpandedId(isOpen ? null : run.id)}>
                                {isOpen ? <CollapseIcon /> : <ExpandIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" fontWeight={600}>{run.client_name}</Typography>
                                {(() => {
                                  const audit = auditByClient[run.client];
                                  if (!audit || audit.overall_status === 'green') return null;
                                  const isRed = audit.overall_status === 'red';
                                  return (
                                    <Tooltip
                                      title={`Geo audit ${audit.overall_status.toUpperCase()} (${audit.audit_date})${audit.notes ? ': ' + audit.notes : ''}${isRed ? '. Approve will require override.' : ''}`}
                                    >
                                      <Chip
                                        label={isRed ? 'Audit ⚠' : 'Audit ⓘ'}
                                        size="small"
                                        color={isRed ? 'error' : 'warning'}
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                      />
                                    </Tooltip>
                                  );
                                })()}
                              </Stack>
                            </TableCell>
                            <TableCell>{fmtTime(run.created_at)}</TableCell>
                            <TableCell align="center" sx={HIDE_ON_MOBILE_SX}>{s.budget_change_count || 0}</TableCell>
                            <TableCell align="center" sx={HIDE_ON_MOBILE_SX}>{s.value_rule_change_count || 0}</TableCell>
                            <TableCell align="right" sx={HIDE_ON_MOBILE_SX}>{fmtCents(currentCents)}</TableCell>
                            <TableCell align="right" sx={HIDE_ON_MOBILE_SX}>
                              {rulesOnly ? <Typography variant="body2" color="text.secondary">—</Typography> : fmtCents(recCents)}
                            </TableCell>
                            <TableCell align="right">
                              {rulesOnly ? (
                                <Tooltip title="Rules-only mode: campaign budgets are managed manually; this run only adjusts city bid rules.">
                                  <Chip label="Rules only" size="small" color="info" variant="outlined" />
                                </Tooltip>
                              ) : (
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                  {deltaCents > 0 ? <TrendUpIcon fontSize="small" color="success" /> : deltaCents < 0 ? <TrendDownIcon fontSize="small" color="error" /> : null}
                                  <Typography
                                    variant="body2"
                                    sx={{ color: deltaCents > 0 ? 'success.main' : deltaCents < 0 ? 'error.main' : 'text.secondary' }}
                                  >
                                    {deltaCents > 0 ? '+' : ''}{fmtCents(deltaCents)}
                                  </Typography>
                                </Stack>
                              )}
                            </TableCell>
                            <TableCell align="center"><StatusChip status={run.approval_status} type="approval" /></TableCell>
                            <TableCell align="center"><StatusChip status={run.apply_status} type="apply" /></TableCell>
                            <TableCell align="right">
                              {run.approval_status === 'draft' && (
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                  <Tooltip title="Approve">
                                    <IconButton size="small" color="success" onClick={() => setApproveRun(run)}>
                                      <ApproveIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Reject">
                                    <IconButton size="small" color="error" onClick={() => setRejectRun(run)}>
                                      <RejectIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              )}
                            </TableCell>
                          </TableRow>

                          {/* Inline warning sub-row — always visible (no expand needed) when a
                              run had sub-failures. Lets operators see the error context without
                              hunting for it in the expanded detail or full log. */}
                          {(run.apply_status === 'partial' || run.apply_status === 'failed') && (run.apply_error || run.apply_status === 'failed') && (
                            <TableRow>
                              <TableCell
                                colSpan={12}
                                sx={{
                                  py: 0.5,
                                  px: 2,
                                  bgcolor: run.apply_status === 'failed' ? 'error.50' : 'warning.50',
                                  borderLeft: '3px solid',
                                  borderLeftColor: run.apply_status === 'failed' ? 'error.main' : 'warning.main',
                                }}
                              >
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: run.apply_status === 'failed' ? 'error.main' : 'warning.dark', whiteSpace: 'nowrap' }}>
                                    {run.apply_status === 'failed' ? '✕ Apply failed —' : '⚠ Applied with errors —'}
                                  </Typography>
                                  <Tooltip title={run.apply_error || ''} placement="bottom-start">
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontFamily: 'monospace',
                                      }}
                                    >
                                      {run.apply_error || 'See run log for details.'}
                                    </Typography>
                                  </Tooltip>
                                  <Button
                                    size="small"
                                    variant="text"
                                    sx={{ py: 0, minWidth: 0, fontSize: 11 }}
                                    onClick={() => setExpandedId(isOpen ? null : run.id)}
                                  >
                                    {isOpen ? 'Hide log' : 'View log →'}
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          )}

                          {/* Expanded detail */}
                          <TableRow>
                            <TableCell colSpan={12} sx={{ py: 0, bgcolor: 'action.hover' }}>
                              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                <Box sx={{ py: 2, px: 1 }}>
                                  <Stack spacing={2}>
                                    {/* Mobile-only: run-level values hidden from the table on phones. */}
                                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ display: { xs: 'flex', md: 'none' } }}>
                                      <Chip label={`Budgets: ${s.budget_change_count || 0}`} size="small" variant="outlined" />
                                      <Chip label={`Rules: ${s.value_rule_change_count || 0}`} size="small" variant="outlined" />
                                      <Chip label={`Current: ${fmtCents(currentCents)}`} size="small" variant="outlined" />
                                      <Chip label={rulesOnly ? 'Recommended: —' : `Recommended: ${fmtCents(recCents)}`} size="small" variant="outlined" />
                                    </Stack>
                                    {/* Run context */}
                                    <Stack direction="row" spacing={2} flexWrap="wrap">
                                      <Chip label={`Mode: ${s.mode || 'N/A'}`} size="small" variant="outlined" />
                                      {rulesOnly && (
                                        <Tooltip title="Campaign budgets are managed manually for this client; the optimizer only maintains city bid rules for territory pacing.">
                                          <Chip label="Rules only" size="small" color="info" />
                                        </Tooltip>
                                      )}
                                      <Chip label={`Day ${s.days_elapsed || 0} of ${(s.days_elapsed || 0) + (s.days_remaining || 0)}`} size="small" variant="outlined" />
                                      <Chip label={`MTD: ${fmtUSD.format(Number(s.total_mtd_spend || 0))}`} size="small" variant="outlined" />
                                      {s.deal_stability && <Chip label={`Deals: ${s.deal_stability}`} size="small" variant="outlined" color={s.deal_stability === 'stable' ? 'success' : s.deal_stability === 'volatile' ? 'error' : 'warning'} />}
                                      {s.dow_active && <Chip label="DOW Active" size="small" color="info" />}
                                      {s.pause_flags > 0 && <Chip label={`${s.pause_flags} Pause Flag${s.pause_flags > 1 ? 's' : ''}`} size="small" color="error" />}
                                    </Stack>

                                    {/* Client pacing holds that shaped this run (territory holds
                                        land in city bid rules, not campaign budgets) */}
                                    {Array.isArray(s.pacing_holds_applied) && s.pacing_holds_applied.length > 0 && (
                                      <Alert severity="info" variant="outlined" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                                        <strong>Client pacing adjustments shaping this run:</strong>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                                          {s.pacing_holds_applied.map((h, i) => {
                                            const isBoost = typeof h.pct === 'number' ? h.pct > 0 : Number(h.multiplier) > 1;
                                            const strength = typeof h.pct === 'number'
                                              ? `${h.pct > 0 ? '+' : ''}${h.pct}%`
                                              : `×${h.multiplier}`;
                                            return (
                                              <Chip
                                                key={h.hold_id || i}
                                                size="small"
                                                variant="outlined"
                                                color={isBoost ? 'success' : 'warning'}
                                                label={`${h.scope_label || `${h.scope_type} ${h.scope_id}`}: ${strength}${h.backfill ? ' + backfill' : ''}${h.end_date ? ` (thru ${h.end_date})` : ''}`}
                                              />
                                            );
                                          })}
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                          Territory adjustments are merged into the city bid rules below — they shift spend within the monthly budget, they don't change campaign budgets.
                                        </Typography>
                                      </Alert>
                                    )}

                                    {/* What-if comparison */}
                                    {run.whatif_snapshot?.yesterday_date && (
                                      <Alert severity="info" variant="outlined">
                                        <strong>What-If ({run.whatif_snapshot.yesterday_date}):</strong>{' '}
                                        Yesterday recommended {fmtUSD.format(run.whatif_snapshot.recommended_daily_total || 0)}/day,
                                        actual was {fmtUSD.format(run.whatif_snapshot.actual_daily_total || 0)}/day,
                                        spent {fmtUSD.format(run.whatif_snapshot.actual_spend || 0)}.
                                        {run.whatif_snapshot.recommendation_was_applied ? ' (Applied)' : ' (Not applied)'}
                                      </Alert>
                                    )}

                                    {/* COM-after-apply scoring — did this applied run actually improve cost
                                        of marketing? Lower COM after = better (green). Replaces the old
                                        Performance tab; scores fill in as the run ages (7/14/30d). */}
                                    {run.apply_status === 'applied' && run.baseline_com_pre != null && (() => {
                                      const pct = (v) => (v != null ? `${(Number(v) * 100).toFixed(1)}%` : null);
                                      const baseline = Number(run.baseline_com_pre);
                                      const windows = [
                                        ['Baseline', run.baseline_com_pre, false],
                                        ['7d', run.com_7d_after, true],
                                        ['14d', run.com_14d_after, true],
                                        ['30d', run.com_30d_after, true],
                                      ];
                                      return (
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                            COM after apply:
                                          </Typography>
                                          {windows.map(([label, val, scored]) => {
                                            if (val == null) {
                                              return scored ? (
                                                <Chip key={label} size="small" variant="outlined" label={`${label}: —`} sx={{ color: 'text.disabled' }} />
                                              ) : null;
                                            }
                                            const delta = scored ? (Number(val) - baseline) * 100 : null;
                                            const color = delta == null ? 'default' : delta < 0 ? 'success' : delta > 0 ? 'error' : 'default';
                                            return (
                                              <Chip
                                                key={label}
                                                size="small"
                                                variant="outlined"
                                                color={color}
                                                label={`${label}: ${pct(val)}${delta != null ? ` (${delta > 0 ? '+' : ''}${delta.toFixed(1)}pp)` : ''}`}
                                              />
                                            );
                                          })}
                                        </Stack>
                                      );
                                    })()}

                                    {/* Vertical-level rollup: aggregate the campaign snapshots by vertical so
                                        you can see which verticals shifted up/down before drilling into individual
                                        campaigns. */}
                                    {(() => {
                                      if (!snapshots.length) return null;
                                      const byVertical = {};
                                      for (const cs of snapshots) {
                                        const key = cs.vertical_name || cs.vertical_id || 'Unknown';
                                        if (!byVertical[key]) {
                                          byVertical[key] = { name: key, current: 0, recommended: 0, count: 0 };
                                        }
                                        byVertical[key].current += (cs.current_daily_budget_cents || 0) / 100;
                                        byVertical[key].recommended += (cs.recommended_daily_budget_cents || 0) / 100;
                                        byVertical[key].count += 1;
                                      }
                                      const rows = Object.values(byVertical).sort((a, b) => b.recommended - a.recommended);
                                      if (rows.length <= 1) return null;  // hide if only one vertical
                                      return (
                                        <>
                                          <Typography variant="subtitle2">Vertical Budget Adjustments</Typography>
                                          <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                  <TableCell>Vertical</TableCell>
                                                  <TableCell align="center">Campaigns</TableCell>
                                                  <TableCell align="right">Current</TableCell>
                                                  <TableCell align="right">Recommended</TableCell>
                                                  <TableCell align="right">Change</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {rows.map((v) => {
                                                  const d = v.recommended - v.current;
                                                  const pct = v.current > 0 ? ((d / v.current) * 100).toFixed(1) : '0.0';
                                                  return (
                                                    <TableRow key={v.name} hover>
                                                      <TableCell><strong>{v.name}</strong></TableCell>
                                                      <TableCell align="center">{v.count}</TableCell>
                                                      <TableCell align="right">{fmtUSD.format(v.current)}</TableCell>
                                                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtUSD.format(v.recommended)}</TableCell>
                                                      <TableCell align="right">
                                                        <Typography variant="body2" sx={{ color: d > 0 ? 'success.main' : d < 0 ? 'error.main' : 'text.secondary', fontWeight: 500 }}>
                                                          {d > 0 ? '+' : ''}{fmtUSD.format(d)} ({d > 0 ? '+' : ''}{pct}%)
                                                        </Typography>
                                                      </TableCell>
                                                    </TableRow>
                                                  );
                                                })}
                                              </TableBody>
                                            </Table>
                                          </TableContainer>
                                        </>
                                      );
                                    })()}

                                    <Typography variant="subtitle2">Campaign Budget Adjustments</Typography>
                                    {snapshots.length > 0 ? (
                                      <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                              <TableCell sx={{ minWidth: 350 }}>Campaign</TableCell>
                                              <TableCell>Type</TableCell>
                                              <TableCell align="center">Stage</TableCell>
                                              <TableCell align="right">COM</TableCell>
                                              <TableCell align="right">Current</TableCell>
                                              <TableCell align="right">Recommended</TableCell>
                                              <TableCell align="right">Change</TableCell>
                                              <TableCell sx={{ minWidth: 280 }}>Why</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {snapshots.map((cs, idx) => {
                                              const oldB = (cs.current_daily_budget_cents || 0) / 100;
                                              const newB = (cs.recommended_daily_budget_cents || 0) / 100;
                                              const d = newB - oldB;
                                              const pct = oldB > 0 ? ((d / oldB) * 100).toFixed(1) : '0.0';
                                              const stageColor = cs.maturity_stage === 'mature' ? 'success' : cs.maturity_stage === 'developing' ? 'warning' : 'default';
                                              const comVal = cs.effective_com ? `${(Number(cs.effective_com) * 100).toFixed(0)}%` : '-';
                                              return (
                                                <Fragment key={idx}>
                                                  <TableRow hover>
                                                    <TableCell>
                                                      <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography
                                                          variant="body2"
                                                          component="a"
                                                          href={cs.campaign_id ? `https://www.facebook.com/adsmanager/manage/campaigns?act=&campaign_ids=${cs.campaign_id}` : undefined}
                                                          target="_blank"
                                                          rel="noopener"
                                                          sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                                        >
                                                          {cs.campaign_name || cs.campaign_id}
                                                        </Typography>
                                                        {cs.pause_flag && <Chip label="PAUSE" size="small" color="error" />}
                                                      </Stack>
                                                    </TableCell>
                                                    <TableCell><Chip label={cs.campaign_type || 'Broad'} size="small" variant="outlined" /></TableCell>
                                                    <TableCell align="center">
                                                      <Chip label={`${cs.maturity_stage || '?'} (${cs.com_confidence || '?'})`} size="small" color={stageColor} variant="outlined" />
                                                    </TableCell>
                                                    <TableCell align="right">{comVal}</TableCell>
                                                    <TableCell align="right">{fmtUSD.format(oldB)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtUSD.format(newB)}</TableCell>
                                                    <TableCell align="right">
                                                      <Typography variant="body2" sx={{ color: d > 0 ? 'success.main' : d < 0 ? 'error.main' : 'text.secondary', fontWeight: 500 }}>
                                                        {d > 0 ? '+' : ''}{fmtUSD.format(d)} ({d > 0 ? '+' : ''}{pct}%)
                                                      </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ maxWidth: 350 }}>
                                                        {(cs.why_drivers || []).map((driver, di) => (
                                                          <Chip key={di} label={driver} size="small" variant="outlined" color={driver.includes('better') ? 'success' : driver.includes('worse') || driver.includes('PAUSE') ? 'error' : 'default'} sx={{ mb: 0.5 }} />
                                                        ))}
                                                      </Stack>
                                                    </TableCell>
                                                  </TableRow>
                                                  {cs.pause_flag && cs.pause_reason && (
                                                    <TableRow>
                                                      <TableCell colSpan={8} sx={{ py: 0.5, bgcolor: 'error.50' }}>
                                                        <Typography variant="caption" color="error.main">
                                                          {cs.pause_reason}{cs.redistribute_to ? ` — Redistribute to: ${cs.redistribute_to}` : ''}
                                                        </Typography>
                                                      </TableCell>
                                                    </TableRow>
                                                  )}
                                                </Fragment>
                                              );
                                            })}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        {rulesOnly
                                          ? 'Rules-only mode — campaign budgets are left untouched. Territory pacing is handled by the city bid rules below.'
                                          : 'No campaign budget changes in this run.'}
                                      </Typography>
                                    )}

                                    {/* Value Rule Outcomes — only shown after scoring (≥7 days post-apply).
                                        Validates whether each bucket actually moved leads/spend the expected way. */}
                                    {(run.value_rule_outcomes && run.value_rule_outcomes.length > 0) && (
                                      <>
                                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                                          Value Rule Outcomes (7d before vs 7d after apply)
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined">
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                <TableCell>Vertical</TableCell>
                                                <TableCell align="right">Bucket</TableCell>
                                                <TableCell align="right">Cities</TableCell>
                                                <TableCell align="right">Leads pre → post</TableCell>
                                                <TableCell align="right">Lead lift</TableCell>
                                                <TableCell align="right">CPL pre → post</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {(run.value_rule_outcomes || []).map((o, oi) => {
                                                const lift = o.lead_lift_pct;
                                                const cplPre = o.cpl_pre;
                                                const cplPost = o.cpl_post;
                                                const cplDelta = (cplPre != null && cplPost != null) ? ((cplPost - cplPre) / cplPre) * 100 : null;
                                                // For positive bid: expect leads up. For negative bid: expect leads down (cost cut).
                                                const expectedDir = (o.adjustment_pct || 0) > 0 ? 'up' : 'down';
                                                const liftMatchesExpectation = lift != null && (
                                                  (expectedDir === 'up' && lift > 0) || (expectedDir === 'down' && lift < 0)
                                                );
                                                return (
                                                  <TableRow key={oi} hover>
                                                    <TableCell>{o.vertical_name}</TableCell>
                                                    <TableCell align="right">
                                                      <Chip
                                                        label={`${o.adjustment_pct > 0 ? '+' : ''}${o.adjustment_pct}%`}
                                                        size="small"
                                                        color={o.adjustment_pct > 0 ? 'success' : o.adjustment_pct < 0 ? 'error' : 'default'}
                                                      />
                                                    </TableCell>
                                                    <TableCell align="right">{o.city_count}</TableCell>
                                                    <TableCell align="right">{o.leads_pre} → {o.leads_post}</TableCell>
                                                    <TableCell align="right">
                                                      {lift == null ? '—' : (
                                                        <Typography variant="body2" sx={{ color: liftMatchesExpectation ? 'success.main' : 'error.main', fontWeight: 500 }}>
                                                          {lift > 0 ? '+' : ''}{lift.toFixed(0)}%
                                                        </Typography>
                                                      )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {cplPre == null && cplPost == null ? '—' :
                                                        `${cplPre != null ? '$' + cplPre.toFixed(0) : '—'} → ${cplPost != null ? '$' + cplPost.toFixed(0) : '—'}`
                                                      }
                                                      {cplDelta != null && (
                                                        <Typography component="span" variant="caption" sx={{ ml: 0.5, color: cplDelta < 0 ? 'success.main' : 'error.main' }}>
                                                          ({cplDelta > 0 ? '+' : ''}{cplDelta.toFixed(0)}%)
                                                        </Typography>
                                                      )}
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              })}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </>
                                    )}

                                    {/* Value Rule Adjustments */}
                                    {(() => {
                                      const allRules = run.city_execution_rules_snapshot || [];
                                      if (!allRules.length) return null;
                                      // Older snapshots predate rule_type — fall back to the rule name.
                                      const isTier = r => r.rule_type === 'comscore_tier' || /Pacing Tier/.test(r.name || '');
                                      const tierRules = allRules.filter(isTier);
                                      const vrRules = allRules.filter(r => !isTier(r));
                                      const totalUp = vrRules.filter(r => r.adjustment_pct > 0).reduce((sum, r) => sum + (r.city_count || 0), 0);
                                      const totalDown = vrRules.filter(r => r.adjustment_pct < 0).reduce((sum, r) => sum + (r.city_count || 0), 0);
                                      const verticals = [...new Set(allRules.map(r => r.vertical_name).filter(Boolean))];
                                      const tierMarketsDown = tierRules.filter(r => r.adjustment_pct < 0).reduce((sum, r) => sum + (r.city_count || 0), 0);
                                      const tierMarketsUp = tierRules.filter(r => r.adjustment_pct > 0).reduce((sum, r) => sum + (r.city_count || 0), 0);
                                      return (
                                        <>
                                          {tierRules.length > 0 && (
                                            <>
                                              <Typography variant="subtitle2" sx={{ mt: 1 }}>Market Pacing Tiers (Comscore)</Typography>
                                              <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                                                <Chip label={`${tierMarketsDown} markets bid down`} size="small" color="error" />
                                                <Chip label={`${tierMarketsUp} markets bid up`} size="small" color="success" />
                                              </Stack>
                                              <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                                                <Table size="small">
                                                  <TableHead>
                                                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                      <TableCell>Vertical</TableCell>
                                                      <TableCell align="right">Tier</TableCell>
                                                      <TableCell align="right">Markets</TableCell>
                                                      <TableCell>Market Names</TableCell>
                                                    </TableRow>
                                                  </TableHead>
                                                  <TableBody>
                                                    {tierRules.map((tr, ti) => {
                                                      const markets = tr.market_names || [];
                                                      const trKey = `${run.id}-tier-${ti}`;
                                                      const expanded = expandedVrIds[trKey] || false;
                                                      return (
                                                        <TableRow key={ti} hover>
                                                          <TableCell>
                                                            <Chip label={tr.vertical_name || '-'} size="small" variant="outlined" />
                                                          </TableCell>
                                                          <TableCell align="right">
                                                            <Chip
                                                              label={`${tr.adjustment_pct > 0 ? '+' : ''}${tr.adjustment_pct}%`}
                                                              size="small"
                                                              color={tr.adjustment_pct > 0 ? 'success' : 'error'}
                                                            />
                                                          </TableCell>
                                                          <TableCell align="right">{tr.city_count}</TableCell>
                                                          <TableCell>
                                                            {markets.length === 0 ? (
                                                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>—</Typography>
                                                            ) : markets.length <= 5 ? (
                                                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                                                {markets.join(', ')}
                                                              </Typography>
                                                            ) : (
                                                              <>
                                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                                                  {expanded ? markets.join(', ') : markets.slice(0, 3).join(', ')}
                                                                </Typography>
                                                                <Button
                                                                  size="small"
                                                                  sx={{ fontSize: 11, p: 0, minWidth: 0, ml: 0.5 }}
                                                                  onClick={() => setExpandedVrIds(prev => ({ ...prev, [trKey]: !expanded }))}
                                                                >
                                                                  {expanded ? 'Show less' : `+${markets.length - 3} more`}
                                                                </Button>
                                                              </>
                                                            )}
                                                          </TableCell>
                                                        </TableRow>
                                                      );
                                                    })}
                                                  </TableBody>
                                                </Table>
                                              </TableContainer>
                                            </>
                                          )}
                                          <Typography variant="subtitle2" sx={{ mt: 1 }}>Value Rule Bid Adjustments</Typography>
                                          <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                                            <Chip label={`${totalUp} cities bid up`} size="small" color="success" />
                                            <Chip label={`${totalDown} cities bid down`} size="small" color="error" />
                                            <Chip label={`${allRules.length} rules across ${verticals.length} vertical${verticals.length !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                                          </Stack>
                                          <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                  <TableCell>Vertical</TableCell>
                                                  <TableCell align="right">Bid</TableCell>
                                                  <TableCell align="right">Cities</TableCell>
                                                  <TableCell>City Names</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {vrRules.map((vr, vi) => {
                                                  const cities = vr.city_names || [];
                                                  const vrKey = `${run.id}-${vi}`;
                                                  const expanded = expandedVrIds[vrKey] || false;
                                                  return (
                                                    <TableRow key={vi} hover>
                                                      <TableCell>
                                                        <Chip label={vr.vertical_name || '-'} size="small" variant="outlined" />
                                                      </TableCell>
                                                      <TableCell align="right">
                                                        <Chip
                                                          label={`${vr.adjustment_pct > 0 ? '+' : ''}${vr.adjustment_pct}%`}
                                                          size="small"
                                                          color={vr.adjustment_pct > 0 ? 'success' : 'error'}
                                                        />
                                                      </TableCell>
                                                      <TableCell align="right">{vr.city_count}</TableCell>
                                                      <TableCell>
                                                        {cities.length <= 5 ? (
                                                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                                            {cities.join(', ')}
                                                          </Typography>
                                                        ) : (
                                                          <>
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                                              {expanded ? cities.join(', ') : cities.slice(0, 3).join(', ')}
                                                            </Typography>
                                                            <Button
                                                              size="small"
                                                              sx={{ fontSize: 11, p: 0, minWidth: 0, ml: 0.5 }}
                                                              onClick={() => setExpandedVrIds(prev => ({ ...prev, [vrKey]: !expanded }))}
                                                            >
                                                              {expanded ? 'Show less' : `+${cities.length - 3} more`}
                                                            </Button>
                                                          </>
                                                        )}
                                                      </TableCell>
                                                    </TableRow>
                                                  );
                                                })}
                                              </TableBody>
                                            </Table>
                                          </TableContainer>
                                        </>
                                      );
                                    })()}

                                    {run.apply_error && (
                                      <Alert severity={run.apply_status === 'failed' ? 'error' : 'warning'} variant="outlined">
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                          {run.apply_status === 'failed' ? 'Apply failed' : 'Applied with errors'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', whiteSpace: 'pre-wrap', mb: 1 }}>
                                          {run.apply_error}
                                        </Typography>
                                        {(() => {
                                          const fc = failedChangesByRun[run.id];
                                          if (!fc) return null;
                                          if (fc.loading) return <Typography variant="caption">Loading change details…</Typography>;
                                          if (fc.error) return <Typography variant="caption" color="error">{fc.error}</Typography>;
                                          if (!fc.items.length) return null;
                                          return (
                                            <>
                                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mt: 1 }}>
                                                Failed changes ({fc.items.length}):
                                              </Typography>
                                              <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
                                                <Table size="small">
                                                  <TableHead>
                                                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                      <TableCell>Type</TableCell>
                                                      <TableCell>Target</TableCell>
                                                      <TableCell>Change</TableCell>
                                                      <TableCell>Error</TableCell>
                                                    </TableRow>
                                                  </TableHead>
                                                  <TableBody>
                                                    {fc.items.map((c) => (
                                                      <TableRow key={c.id}>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                          <Chip label={c.change_type || '?'} size="small" variant="outlined" />
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>
                                                          {c.campaign_name || c.entity_id || '—'}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                          {fmtChangeValue(c.previous_value)} → {fmtChangeValue(c.new_value)}
                                                        </TableCell>
                                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'error.main', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                          {c.error_message}
                                                        </TableCell>
                                                      </TableRow>
                                                    ))}
                                                  </TableBody>
                                                </Table>
                                              </TableContainer>
                                            </>
                                          );
                                        })()}
                                      </Alert>
                                    )}
                                  </Stack>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={Boolean(approveRun)} onClose={() => setApproveRun(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve & Apply to Facebook</DialogTitle>
        <DialogContent>
          {approveRun && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning" variant="outlined">
                This will push changes to Facebook Ads Manager for <strong>{approveRun.client_name}</strong>.
              </Alert>
              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Budget Changes</Typography>
                  <Typography variant="h6">{approveRun.summary_snapshot?.budget_change_count || 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Value Rule Sets</Typography>
                  <Typography variant="h6">{approveRun.summary_snapshot?.value_rule_change_count || 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Daily Budget</Typography>
                  {approveRun.summary_snapshot?.rules_only ? (
                    <Typography variant="h6" color="text.secondary">Rules only — unchanged</Typography>
                  ) : (
                    <Typography variant="h6">
                      {fmtCents(approveRun.summary_snapshot?.current_daily_budget_total_cents)} {' -> '}
                      {fmtCents(approveRun.summary_snapshot?.recommended_daily_budget_total_cents)}
                    </Typography>
                  )}
                </Box>
              </Stack>
              {actionError && !blockingAudit && <Alert severity="error">{actionError}</Alert>}
              {blockingAudit && (
                <Alert severity="error" variant="filled">
                  <strong>Geo data audit is RED for this client</strong> ({blockingAudit.audit_date}).<br />
                  {blockingAudit.notes || 'Multiple data-quality checks failed.'}<br />
                  <Box sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={overrideAck}
                          onChange={(e) => setOverrideAck(e.target.checked)}
                          sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                        />
                      )}
                      label="I acknowledge the data quality issue and want to apply anyway"
                    />
                  </Box>
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setApproveRun(null); setBlockingAudit(null); setOverrideAck(false); }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleApprove(blockingAudit ? overrideAck : false)}
            variant="contained"
            color={blockingAudit ? 'error' : 'success'}
            disabled={actionLoading || (blockingAudit && !overrideAck)}
          >
            {actionLoading ? <CircularProgress size={20} /> : (blockingAudit ? 'Override & Apply' : 'Confirm & Apply')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={Boolean(rejectRun)} onClose={() => setRejectRun(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Optimizer Run</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography>Reject this run for <strong>{rejectRun?.client_name}</strong>? No changes will be applied.</Typography>
            <TextField
              label="Notes (optional)"
              multiline
              rows={2}
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectRun(null); setRejectNotes(''); }} disabled={actionLoading}>Cancel</Button>
          <Button onClick={handleReject} variant="contained" color="error" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── City Bid Buckets Editor ─────────────────────────────────────

const DEFAULT_CITY_BUCKETS = [-80, -60, -40, -20, 15, 25, 40];

function CityBidBucketsEditor({ value, onChange }) {
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState('');
  const buckets = Array.isArray(value) && value.length ? [...value].sort((a, b) => a - b) : [];
  const isDefault = !buckets.length || (
    buckets.length === DEFAULT_CITY_BUCKETS.length
    && buckets.every((b, i) => b === DEFAULT_CITY_BUCKETS[i])
  );

  const remove = (b) => {
    onChange(buckets.filter((x) => x !== b));
  };

  const add = () => {
    setErr('');
    const v = parseInt(draft, 10);
    if (Number.isNaN(v)) { setErr('Enter an integer.'); return; }
    if (v === 0) { setErr('0 is not a valid bucket.'); return; }
    if (v < -90 || v > 90) { setErr('Must be between -90 and +90.'); return; }
    if (buckets.includes(v)) { setErr(`${v} is already in the list.`); return; }
    if (buckets.length >= 9) { setErr('Max 9 buckets.'); return; }
    onChange([...buckets, v].sort((a, b) => a - b));
    setDraft('');
  };

  const reset = () => {
    onChange([...DEFAULT_CITY_BUCKETS]);
    setDraft('');
    setErr('');
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {(buckets.length ? buckets : DEFAULT_CITY_BUCKETS).map((b) => (
          <Chip
            key={b}
            label={`${b > 0 ? '+' : ''}${b}%`}
            color={b > 0 ? 'success' : 'error'}
            variant={buckets.length ? 'filled' : 'outlined'}
            onDelete={buckets.length ? () => remove(b) : undefined}
            size="small"
          />
        ))}
        {!buckets.length && (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
            (using defaults)
          </Typography>
        )}
      </Stack>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          label="Add bucket %"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          error={!!err}
          helperText={err || ' '}
          sx={{ width: 160 }}
          inputProps={{ inputMode: 'numeric' }}
        />
        <Button variant="outlined" size="small" onClick={add} sx={{ mt: 0.5 }}>Add</Button>
        <Button size="small" onClick={reset} sx={{ mt: 0.5 }}>Reset to defaults</Button>
      </Stack>
    </Stack>
  );
}

// ── Inline-editable numeric table cell ──────────────────────────
// Lets operators edit an existing override value in place instead of
// removing and re-adding it. Buffers keystrokes locally and commits the
// parsed/clamped value on blur or Enter (onCommit(null) clears the value).

function InlineNumberCell({
  value,
  onCommit,
  placeholder = 'Global',
  prefix,
  suffix,
  width = 96,
  min,
  max,
  zeroIsEmpty = false,
  rejectZero = false,
  textColor,
}) {
  const fmt = (v) => (v == null || v === '' ? '' : String(v));
  const [text, setText] = useState(fmt(value));
  useEffect(() => { setText(fmt(value)); }, [value]);

  const commit = () => {
    const t = text.trim();
    if (t === '' || t === '-' || t === '+') { onCommit(null); return; }
    let n = Number(t);
    if (!Number.isFinite(n)) { setText(fmt(value)); return; } // revert garbage
    n = Math.round(n);
    if (min != null && n < min) n = min;
    if (max != null && n > max) n = max;
    if (n === 0 && rejectZero) { setText(fmt(value)); return; } // 0 not valid → revert
    if (n === 0 && zeroIsEmpty) { onCommit(null); return; }
    onCommit(n);
  };

  return (
    <TextField
      size="small"
      value={text}
      placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); e.target.blur(); } }}
      InputProps={{
        startAdornment: prefix ? <Typography variant="caption" sx={{ mr: 0.25 }}>{prefix}</Typography> : undefined,
        endAdornment: suffix ? <Typography variant="caption" sx={{ ml: 0.25 }}>{suffix}</Typography> : undefined,
      }}
      inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
      sx={{ width, ...(textColor ? { '& .MuiInputBase-input': { color: textColor } } : {}) }}
    />
  );
}

// ── Settings Tab ────────────────────────────────────────────────

function OptimizerSettingsTab({ clients, initialClientId = '' }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [editConfig, setEditConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState({ type: '', text: '' });
  const [clientCampaigns, setClientCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [overrideCampaignId, setOverrideCampaignId] = useState('');
  const [overrideMin, setOverrideMin] = useState('');
  const [overrideMax, setOverrideMax] = useState('');
  const [clientTerritories, setClientTerritories] = useState([]);
  const [territoriesLoading, setTerritoriesLoading] = useState(false);
  const [clientVerticals, setClientVerticals] = useState([]);
  const [territoryOverrideId, setTerritoryOverrideId] = useState('');
  const [territoryOverrideVerticalId, setTerritoryOverrideVerticalId] = useState('all');
  const [territoryOverridePct, setTerritoryOverridePct] = useState('');
  const [territoryOverrideBackfill, setTerritoryOverrideBackfill] = useState(false);
  const [territoryOverrideExpires, setTerritoryOverrideExpires] = useState('');
  const [clientPacingHolds, setClientPacingHolds] = useState([]);
  const [globalMultiplier, setGlobalMultiplier] = useState('');
  const [globalSavedMultiplier, setGlobalSavedMultiplier] = useState('');
  const [globalSaving, setGlobalSaving] = useState(false);
  const [globalMsg, setGlobalMsg] = useState({ type: '', text: '' });
  const [globalV2, setGlobalV2] = useState(false);
  const [globalV2Saving, setGlobalV2Saving] = useState(false);

  useEffect(() => {
    fetchOptimizerGlobalConfig()
      .then((res) => {
        const v = res.data?.pacing_boost_multiplier ?? '1.00';
        setGlobalMultiplier(String(v));
        setGlobalSavedMultiplier(String(v));
        setGlobalV2(Boolean(res.data?.controller_v2_enabled));
      })
      .catch(() => {});
  }, []);

  const saveGlobalV2 = (checked) => {
    setGlobalV2(checked);
    setGlobalV2Saving(true);
    updateOptimizerGlobalConfig({ controller_v2_enabled: checked })
      .then((res) => setGlobalV2(Boolean(res.data?.controller_v2_enabled)))
      .catch((err) => {
        setGlobalV2(!checked);
        setGlobalMsg({ type: 'error', text: err?.response?.data?.detail || err?.message || 'Save failed.' });
      })
      .finally(() => setGlobalV2Saving(false));
  };

  const saveGlobalMultiplier = () => {
    setGlobalSaving(true);
    setGlobalMsg({ type: '', text: '' });
    updateOptimizerGlobalConfig({ pacing_boost_multiplier: globalMultiplier })
      .then((res) => {
        const v = String(res.data?.pacing_boost_multiplier ?? globalMultiplier);
        setGlobalMultiplier(v);
        setGlobalSavedMultiplier(v);
        setGlobalMsg({ type: 'success', text: 'Saved. Applies to every client on their next optimizer run.' });
      })
      .catch((err) => setGlobalMsg({
        type: 'error',
        text: err?.response?.data?.pacing_boost_multiplier?.[0]
          || err?.response?.data?.detail || err?.message || 'Save failed.',
      }))
      .finally(() => setGlobalSaving(false));
  };

  const loadConfigs = useCallback(() => {
    setLoading(true);
    fetchOptimizerConfigs()
      .then((res) => setConfigs(res.data?.results || res.data || []))
      .catch((err) => setError(err?.message || 'Failed'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const openEditor = (clientId) => {
    setSelectedClientId(String(clientId));
    setEditOpen(true);
    setSaveMsg({ type: '', text: '' });
  };

  // Deep-link from Client Pulse (?client_id=…&tab=settings): open that client's
  // editor straight away. The editConfig effect resolves an existing config vs.
  // a new one once configs finish loading. Runs once on mount.
  useEffect(() => {
    if (initialClientId) openEditor(initialClientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeEditor = () => {
    setEditOpen(false);
    setSelectedClientId('');
    setEditConfig(null);
  };

  useEffect(() => {
    if (!selectedClientId) { setEditConfig(null); return; }
    const existing = configs.find((c) => c.client === Number(selectedClientId));
    setEditConfig(existing
      ? { ...existing }
      : {
          client: Number(selectedClientId),
          enabled: false,
          mode: 'dry_run',
          rules_only: false,
          segment_budget_caps_enabled: false,
          budget_cap_enabled: true,
          controller_v2: null,
          territory_adherence_pct: 30,
          vertical_adherence_pct: 30,
          com_priority_weight: '0.60',
          broad_local_split_broad_pct: 85,
          min_daily_budget_cents: 15000,
          max_daily_budget_cents: 500000,
          max_daily_change_pct: 30,
          com_lookback_days: 30,
          dow_enabled: false,
          dow_min_data_days: 60,
          dow_max_adjustment_pct: 20,
          pause_flag_enabled: true,
          pause_flag_threshold_multiplier: '3.0',
          campaign_budget_overrides: {},
          territory_bid_overrides: {},
          city_bid_buckets: [-80, -60, -40, -20, 15, 25, 40],
        }
    );
    setSaveMsg({ type: '', text: '' });
  }, [selectedClientId, configs]);

  // Load campaigns for override selection
  useEffect(() => {
    if (!selectedClientId) { setClientCampaigns([]); return; }
    setCampaignsLoading(true);
    import('@src/api/clients/default').then(({ default: client }) => {
      client.request({
        url: `dashboard/facebook/ad-structure-db/?client_id=${selectedClientId}`,
        method: 'GET',
      })
        .then((res) => {
          const accounts = res.data || [];
          const campaigns = [];
          for (const acct of accounts) {
            for (const camp of (acct.campaigns || [])) {
              campaigns.push({ id: camp.campaign_id, name: camp.campaign_name || camp.campaign_id });
            }
          }
          setClientCampaigns(campaigns);
        })
        .catch(() => setClientCampaigns([]))
        .finally(() => setCampaignsLoading(false));
    });
  }, [selectedClientId]);

  // Load territories for the manual override picker
  useEffect(() => {
    if (!selectedClientId) { setClientTerritories([]); return; }
    setTerritoriesLoading(true);
    fetchClientTerritories(selectedClientId)
      .then((res) => {
        const data = res.data?.results || res.data || [];
        setClientTerritories(Array.isArray(data) ? data : []);
      })
      .catch(() => setClientTerritories([]))
      .finally(() => setTerritoriesLoading(false));
  }, [selectedClientId]);

  // Load verticals so the operator can scope overrides per-vertical
  useEffect(() => {
    if (!selectedClientId) { setClientVerticals([]); return; }
    fetchVerticalsForClient(selectedClientId)
      .then((res) => {
        const data = res.data?.results || res.data || [];
        setClientVerticals(Array.isArray(data) ? data : []);
      })
      .catch(() => setClientVerticals([]));
  }, [selectedClientId]);

  // Active client-submitted pacing holds — shown read-only next to the manual
  // overrides because the optimizer layers them in at run time (they never
  // appear in territory_bid_overrides itself).
  useEffect(() => {
    if (!selectedClientId) { setClientPacingHolds([]); return; }
    fetchPacingHolds({ client_id: selectedClientId, active: 'true' })
      .then((res) => {
        const data = res.data?.results || res.data || [];
        setClientPacingHolds(Array.isArray(data) ? data : []);
      })
      .catch(() => setClientPacingHolds([]));
  }, [selectedClientId]);

  const handleSave = () => {
    if (!editConfig) return;
    setSaving(true);
    setSaveMsg({ type: '', text: '' });
    const action = editConfig.id
      ? updateOptimizerConfig(editConfig.id, editConfig)
      : createOptimizerConfig(editConfig);
    action
      .then(() => { setSaveMsg({ type: 'success', text: 'Configuration saved successfully.' }); loadConfigs(); closeEditor(); })
      .catch((err) => setSaveMsg({ type: 'error', text: err?.response?.data?.detail || JSON.stringify(err?.response?.data) || err?.message || 'Save failed.' }))
      .finally(() => setSaving(false));
  };

  const set = (field, value) => setEditConfig((prev) => ({ ...prev, [field]: value }));
  // Brands of a parent that pools value rules share one rule set per vertical,
  // so Pacing-First is the parent's switch: it reads and writes the parent.
  const pooledParent = editConfig?.pooled_parent || null;
  const pacingFirstOn = pooledParent
    ? Boolean(editConfig?.parent_pacing_first_enabled ?? pooledParent.pacing_first_enabled)
    : Boolean(editConfig?.pacing_first_enabled);

  const modeDescriptions = {
    dry_run: 'Logs recommended changes but does not touch Facebook. Use this to validate the algorithm.',
    manual_approve: 'Creates runs that require manual approval before applying to Facebook.',
    auto_apply: 'Fully automated. Changes are applied to Facebook daily without approval.',
  };

  const filteredConfigs = useMemo(() => {
    if (!searchQuery) return configs;
    const q = searchQuery.toLowerCase();
    return configs.filter((c) => (c.client_name || '').toLowerCase().includes(q));
  }, [configs, searchQuery]);

  const globalBoostActive = Number(globalSavedMultiplier || 1) !== 1;
  const globalDirty = globalMultiplier !== globalSavedMultiplier;

  return (
    <>
      {/* Global settings — applies to every client */}
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title="Global Settings"
          subheader="Applies to ALL optimizer-managed clients on their next run"
          action={globalBoostActive && (
            <Chip label={`Pacing boost ${globalSavedMultiplier}x active`} color="warning" size="small" />
          )}
        />
        <CardContent sx={{ pt: 0 }}>
          {globalMsg.text && <Alert severity={globalMsg.type} sx={{ mb: 2 }}>{globalMsg.text}</Alert>}
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Pacing boost multiplier"
              size="small"
              value={globalMultiplier}
              onChange={(e) => setGlobalMultiplier(e.target.value)}
              inputProps={{ inputMode: 'decimal' }}
              sx={{ width: 200 }}
            />
            <Button
              variant="contained"
              size="small"
              disabled={globalSaving || !globalDirty}
              onClick={saveGlobalMultiplier}
            >
              {globalSaving ? 'Saving…' : 'Save'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 560 }}>
              Multiplies every client&apos;s remaining-per-day daily target before budgets are
              pushed to Facebook (1.00 = off, 1.25 = send 25% more). Use when campaigns are
              systematically underspending their FB daily budgets. Range 0.50–2.00. Month
              totals stay protected: targets recompute daily from actual remaining budget,
              and the budget-cap auto-pause still hard-stops at the committed monthly budget.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={globalV2}
                  disabled={globalV2Saving}
                  onChange={(e) => saveGlobalV2(e.target.checked)}
                  color="secondary"
                />
              )}
              label={<Typography variant="body2" fontWeight={600}>Controller v2 (all clients)</Typography>}
            />
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 640 }}>
              New pacing controller: corrects territory pacing continuously (no ±band dead
              zone — the adherence slider becomes correction strength), escalates when a
              territory stays off-pace, reserves performance bid-ups for clearly-proven
              cities, adds the −30 bucket, re-arms the per-run change cap, and glides
              hard-cap clients into their caps instead of slamming. Per-client override
              in each client&apos;s settings wins over this switch. Run
              <code style={{ margin: '0 4px' }}>optimizer_v2_diff</code> before flipping.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* Config summary table */}
      <Card>
        <CardHeader
          title="Configured Clients"
          subheader={`${configs.filter((c) => c.enabled).length} of ${configs.length} clients enabled`}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              {loading && <CircularProgress size={20} />}
              <Button variant="contained" size="small" onClick={() => { setSelectedClientId(''); setEditOpen(true); setSaveMsg({ type: '', text: '' }); }}>
                Add Client
              </Button>
            </Stack>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <TextField
            placeholder="Search clients..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
          />
        </CardContent>
        {error && <Alert severity="error" sx={{ mx: 2 }}>{error}</Alert>}
        {filteredConfigs.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell align="center">COM Weight</TableCell>
                  <TableCell align="center">Broad / Local</TableCell>
                  <TableCell align="center">Min / Max</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredConfigs.map((cfg) => (
                  <TableRow key={cfg.id} hover sx={{ cursor: 'pointer' }} onClick={() => openEditor(cfg.client)}>
                    <TableCell><Typography variant="body2" fontWeight={600}>{cfg.client_name}</Typography></TableCell>
                    <TableCell align="center">
                      <Chip label={cfg.enabled ? 'Active' : 'Off'} color={cfg.enabled ? 'success' : 'default'} size="small" variant={cfg.enabled ? 'filled' : 'outlined'} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Chip label={cfg.mode.replace(/_/g, ' ')} size="small" variant="outlined" />
                        {cfg.rules_only && <Chip label="rules only" size="small" color="info" variant="outlined" />}
                        {cfg.pacing_boost_multiplier != null && (
                          <Chip label={`boost ${Number(cfg.pacing_boost_multiplier)}x`} size="small" color="warning" variant="outlined" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">{Math.round(Number(cfg.com_priority_weight) * 100)}%</TableCell>
                    <TableCell align="center">{cfg.broad_local_split_broad_pct} / {100 - cfg.broad_local_split_broad_pct}</TableCell>
                    <TableCell align="center">${Math.round(cfg.min_daily_budget_cents / 100)} / ${Math.round(cfg.max_daily_budget_cents / 100)}</TableCell>
                    <TableCell>
                      <IconButton size="small"><SettingsIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Editor Dialog */}
      <Dialog open={editOpen} onClose={closeEditor} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>
          {editConfig?.id
            ? `Edit: ${clients.find((c) => c.id === Number(selectedClientId))?.name || ''}`
            : 'Configure Client'
          }
        </DialogTitle>
        <DialogContent dividers sx={{ p: 4 }}>
          <Stack spacing={3}>
            {saveMsg.text && <Alert severity={saveMsg.type}>{saveMsg.text}</Alert>}

            {!editConfig?.id && (
              <Autocomplete
                options={clients}
                getOptionLabel={(opt) => opt.name || ''}
                value={clients.find((c) => c.id === Number(selectedClientId)) || null}
                onChange={(_, val) => setSelectedClientId(val?.id ? String(val.id) : '')}
                renderInput={(params) => <TextField {...params} label="Client" />}
              />
            )}

            {editConfig && (
              <>
                <Divider />

                <FormControlLabel
                  control={<Switch checked={editConfig.enabled} onChange={(e) => set('enabled', e.target.checked)} color="success" />}
                  label={
                    <Stack>
                      <Typography variant="body1">Optimizer Enabled</Typography>
                      <Typography variant="caption" color="text.secondary">When enabled, the optimizer will generate daily runs for this client.</Typography>
                    </Stack>
                  }
                />

                <FormControl fullWidth>
                  <InputLabel>Mode</InputLabel>
                  <Select value={editConfig.mode} label="Mode" onChange={(e) => set('mode', e.target.value)}>
                    <MenuItem value="dry_run">Dry Run</MenuItem>
                    <MenuItem value="manual_approve">Manual Approve</MenuItem>
                    <MenuItem value="auto_apply">Auto Apply</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
                  {modeDescriptions[editConfig.mode]}
                </Typography>

                <FormControlLabel
                  control={<Switch checked={Boolean(editConfig.rules_only)} onChange={(e) => set('rules_only', e.target.checked)} color="info" />}
                  label={
                    <Stack>
                      <Typography variant="body1">Rules Only (manual budgets)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Skip all campaign budget recommendations and pause flags — budgets stay exactly as set in Ads Manager.
                        Runs still compute and apply city bid value rules to every active campaign, so territory pacing stays automated.
                        Use for clients where COM history is misleading (e.g. account relaunches) and budgets are managed by hand.
                      </Typography>
                    </Stack>
                  }
                />

                <FormControlLabel
                  control={<Switch checked={Boolean(editConfig.segment_budget_caps_enabled)} onChange={(e) => set('segment_budget_caps_enabled', e.target.checked)} color="warning" />}
                  label={
                    <Stack>
                      <Typography variant="body1">Hard Segment Budget Caps</Typography>
                      <Typography variant="caption" color="text.secondary">
                        For strict-budget clients only: each budget segment (territory and/or vertical) is a hard wall.
                        When a segment hits its month amount (incl. fees) it is auto turned off — its ZIPs drop from the broad campaigns,
                        or the vertical&apos;s campaigns pause if nothing is left. Staff-only alert, no client email.
                        Resume is human-only via the month-rollover approval card on /tasks.
                        Also ramps city bids down once a segment passes 85% of its budget so it glides into the cap.
                      </Typography>
                    </Stack>
                  }
                />

                <FormControlLabel
                  control={<Switch checked={editConfig.budget_cap_enabled !== false} onChange={(e) => set('budget_cap_enabled', e.target.checked)} color="warning" />}
                  label={
                    <Stack>
                      <Typography variant="body1">Overall Budget Cap (hard stop)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pause ALL campaigns once month-to-date spend (incl. fees) reaches the committed monthly budget.
                        ON by default — it protects against overspend. Resume is human-only via the /tasks approval card.
                      </Typography>
                    </Stack>
                  }
                />

                <FormControl fullWidth>
                  <InputLabel>Pacing Controller</InputLabel>
                  <Select
                    value={editConfig.controller_v2 === true ? 'on' : editConfig.controller_v2 === false ? 'off' : 'inherit'}
                    label="Pacing Controller"
                    onChange={(e) => {
                      const v = e.target.value;
                      set('controller_v2', v === 'on' ? true : v === 'off' ? false : null);
                    }}
                  >
                    <MenuItem value="inherit">Inherit global setting</MenuItem>
                    <MenuItem value="on">Controller v2 ON (continuous + escalating)</MenuItem>
                    <MenuItem value="off">Controller v1 (legacy ±band trigger)</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
                  v2 corrects territory pacing continuously (the adherence slider becomes correction
                  strength instead of a do-nothing band), escalates after 3+ days off-pace, merit-gates
                  performance bid-ups, and glides hard-cap segments into their caps. Use this to flip a
                  single client early (e.g. a late-month over-pacer) before the global switch.
                </Typography>

                <FormControlLabel
                  control={<Switch checked={Boolean(editConfig.comscore_tier_enabled)} onChange={(e) => set('comscore_tier_enabled', e.target.checked)} color="secondary" />}
                  label={
                    <Stack>
                      <Typography variant="body1">Comscore Pacing Tier (long-tail clients)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Requires Controller v2. Adds trailing market-level value rules that carry each
                        off-pace territory&apos;s pacing correction to the thousands of small cities the
                        50-per-bucket city rules can&apos;t list. City bids always match first; territories
                        with manual overrides are skipped. For strict long-tail clients (Premier-type) —
                        most clients don&apos;t need this.
                      </Typography>
                    </Stack>
                  }
                />

                <FormControlLabel
                  control={<Switch checked={pacingFirstOn} onChange={(e) => set(pooledParent ? 'parent_pacing_first_enabled' : 'pacing_first_enabled', e.target.checked)} color="secondary" />}
                  label={
                    <Stack>
                      <Typography variant="body1">Pacing-First Controller (2026-09 redesign)</Typography>
                      {pooledParent && (
                        <Typography variant="caption" color="secondary" sx={{ display: 'block', fontWeight: 600 }}>
                          Shared rule set: this switch turns Pacing-First on or off for all {pooledParent.brand_count} brands
                          of {pooledParent.name} at once. A market two brands both advertise in gets a bid only when the
                          brands buying most of it agree on direction.
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Requires Controller v2. Keep every territory inside its band, then minimize
                        cost of marketing in the slack. One package: the Comscore tier bids markets a
                        territory dominates (85%+ or sole claimant), and shared markets only when the
                        territories sharing them agree on direction (a disagreement is logged as a
                        conflict — merge the budgets or split the campaigns); a city gets its own
                        bid only with 150+ of its own leads and a clear result — the rest get no rule
                        and Meta decides; an up-ladder (+40/+60/+100/+150) for under-paced markets
                        that beat the client&apos;s CoM, capped by headroom; a city with its own rule
                        bids its territory&apos;s level plus its own edge (slowing or catching up); and a
                        per-territory decision log on every run. Stage 2 (band positions) is the
                        switch below. Roll out in waves — Home Genius and Premier first.
                      </Typography>
                    </Stack>
                  }
                />

                <FormControlLabel
                  sx={{ ml: 3 }}
                  control={<Switch checked={Boolean(editConfig.band_positions_enabled)} disabled={!pacingFirstOn} onChange={(e) => set('band_positions_enabled', e.target.checked)} color="secondary" />}
                  label={
                    <Stack>
                      <Typography variant="body1">Stage 2 — Band Positions</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Aim each territory inside the adherence band by CoM: best toward the top of
                        the band, worst toward the bottom, client total unchanged. Off = every
                        territory aims at its nominal budget. Turn on after the ladders have been
                        read for a few runs, so the decision log isn&apos;t explaining two changes at
                        once. Requires the pacing-first controller.
                      </Typography>
                    </Stack>
                  }
                />

                <Divider />
                <Typography variant="subtitle2">Budget Allocation</Typography>

                <Box>
                  <Typography variant="body2" gutterBottom>
                    Broad / Local Split: <strong>{editConfig.broad_local_split_broad_pct}%</strong> Broad, <strong>{100 - editConfig.broad_local_split_broad_pct}%</strong> Local
                  </Typography>
                  <Slider
                    value={editConfig.broad_local_split_broad_pct}
                    onChange={(_, v) => set('broad_local_split_broad_pct', v)}
                    min={50} max={100} step={5}
                    marks={[{ value: 50, label: '50/50' }, { value: 85, label: '85/15' }, { value: 100, label: '100/0' }]}
                  />
                </Box>

                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Min Daily Budget"
                    value={editConfig._minDisplay ?? (editConfig.min_daily_budget_cents / 100)}
                    onChange={(e) => {
                      const raw = e.target.value;
                      set('_minDisplay', raw);
                      if (raw !== '') set('min_daily_budget_cents', Math.round(Number(raw) * 100));
                    }}
                    onBlur={() => {
                      set('_minDisplay', undefined);
                    }}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Max Daily Budget"
                    value={editConfig._maxDisplay ?? (editConfig.max_daily_budget_cents / 100)}
                    onChange={(e) => {
                      const raw = e.target.value;
                      set('_maxDisplay', raw);
                      if (raw !== '') set('max_daily_budget_cents', Math.round(Number(raw) * 100));
                    }}
                    onBlur={() => {
                      set('_maxDisplay', undefined);
                    }}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                    sx={{ flex: 1 }}
                  />
                </Stack>

                <Box>
                  <TextField
                    label="Pacing boost override"
                    size="small"
                    value={editConfig.pacing_boost_multiplier ?? ''}
                    onChange={(e) => set('pacing_boost_multiplier', e.target.value === '' ? null : e.target.value)}
                    placeholder={`Global (${globalSavedMultiplier || '1.00'}x)`}
                    inputProps={{ inputMode: 'decimal' }}
                    sx={{ width: 220 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Overrides the global pacing boost for this client only. Blank = inherit
                    global. Set 1.00 to opt this client out of a global boost, or higher for
                    a chronic under-deliverer. Range 0.50–2.00.
                  </Typography>
                </Box>

                <Divider />
                <Typography variant="subtitle2">Optimization Strategy</Typography>

                <Box>
                  <Typography variant="body2" gutterBottom>
                    {(editConfig.controller_v2 === true || (editConfig.controller_v2 == null && globalV2))
                      ? 'Budget Adherence'
                      : 'Territory Adherence Threshold'}: <strong>±{editConfig.territory_adherence_pct}%</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {(editConfig.controller_v2 === true || (editConfig.controller_v2 == null && globalV2)) ? (
                      <>
                        How tightly every budget the client wrote (territory, vertical, segment) is held.
                        Sets the acceptable pacing band (30% → 0.7–1.3x of budget), scales how hard city
                        bids correct inside it, and — for Comscore-enabled clients — triggers escalating
                        market-wide down-bids (−50/−70/−80) for anything persistently outside it.
                        Stricter = narrower band, harder corrections, earlier escalation.
                        {pacingFirstOn && (
                          <> Pacing-first: this is the one dial. The band is the fence; it sets the gain
                          and the chronic edge for both ladders (down to −80, up to +150)
                          {editConfig.band_positions_enabled
                            ? ', and with Stage 2 on, where inside it each territory aims by CoM (best at the top, worst at the bottom)'
                            : ''}
                          . Wider = more room to chase CoM, narrower = tighter pacing.</>
                        )}
                      </>
                    ) : (
                      <>How far territory spend can deviate from budget before the optimizer aggressively corrects (via city bid adjustments).</>
                    )}
                  </Typography>
                  <Slider
                    value={editConfig.territory_adherence_pct}
                    onChange={(_, v) => set('territory_adherence_pct', v)}
                    min={5} max={50} step={5}
                    marks={[{ value: 10, label: 'Strict' }, { value: 30, label: 'Default' }, { value: 50, label: 'Flexible' }]}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" gutterBottom>
                    Optimization Priority: <strong>{Math.round(Number(editConfig.com_priority_weight) * 100)}%</strong> Cost of Marketing / <strong>{100 - Math.round(Number(editConfig.com_priority_weight) * 100)}%</strong> Budget Adherence
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Higher COM weight means the optimizer prioritizes placing spend where cost of marketing is best, even if it deviates from stated budgets.
                    {pacingFirstOn && (
                      <> Mostly dormant under pacing-first: city bids in any territory carrying a market
                      tier ride the tier level plus the city&apos;s own edge and ignore this slider. It still
                      blends city bids in territories with no tier, and the vertical split for clients
                      with vertical budgets (bounded by the adherence band).</>
                    )}
                  </Typography>
                  <Slider
                    value={Math.round(Number(editConfig.com_priority_weight) * 100)}
                    onChange={(_, v) => set('com_priority_weight', (v / 100).toFixed(2))}
                    min={0} max={100} step={5}
                    marks={[{ value: 0, label: 'Budget' }, { value: 60, label: 'Default' }, { value: 100, label: 'COM' }]}
                  />
                </Box>

                <Divider />
                <Typography variant="subtitle2">Day-of-Week Optimization</Typography>

                <FormControlLabel
                  control={<Switch checked={editConfig.dow_enabled || false} onChange={(e) => set('dow_enabled', e.target.checked)} />}
                  label={
                    <Stack>
                      <Typography variant="body1">Day-of-Week Adjustments</Typography>
                      <Typography variant="caption" color="text.secondary">Adjust budgets based on which days convert better. Requires 60+ days of data.</Typography>
                    </Stack>
                  }
                />

                {editConfig.dow_enabled && (
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Min Data Days"
                      value={editConfig._dowDaysDisplay ?? (editConfig.dow_min_data_days || 60)}
                      onChange={(e) => { set('_dowDaysDisplay', e.target.value); if (e.target.value !== '') set('dow_min_data_days', Number(e.target.value)); }}
                      onBlur={() => set('_dowDaysDisplay', undefined)}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Max DOW Adjustment %"
                      value={editConfig._dowPctDisplay ?? (editConfig.dow_max_adjustment_pct || 20)}
                      onChange={(e) => { set('_dowPctDisplay', e.target.value); if (e.target.value !== '') set('dow_max_adjustment_pct', Number(e.target.value)); }}
                      onBlur={() => set('_dowPctDisplay', undefined)}
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                )}

                <Divider />
                <Typography variant="subtitle2">Pause Flagging</Typography>

                <FormControlLabel
                  control={<Switch checked={editConfig.pause_flag_enabled !== false} onChange={(e) => set('pause_flag_enabled', e.target.checked)} />}
                  label={
                    <Stack>
                      <Typography variant="body1">Flag Campaigns for Pause</Typography>
                      <Typography variant="caption" color="text.secondary">Flag campaigns with significantly worse COM than peers. Never auto-pauses — you decide.</Typography>
                    </Stack>
                  }
                />

                {editConfig.pause_flag_enabled !== false && (
                  <TextField
                    label="Pause Threshold (x worse than median)"
                    value={editConfig._pauseDisplay ?? (editConfig.pause_flag_threshold_multiplier || 3.0)}
                    onChange={(e) => { set('_pauseDisplay', e.target.value); if (e.target.value !== '') set('pause_flag_threshold_multiplier', e.target.value); }}
                    onBlur={() => set('_pauseDisplay', undefined)}
                    sx={{ maxWidth: 300 }}
                    helperText="COM must be this many times worse than vertical median"
                  />
                )}

                <Divider />
                <Typography variant="subtitle2">Campaign Budget Overrides</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Set per-campaign min/max daily budgets. Overrides the global min/max for specific campaigns.
                </Typography>

                {/* Existing overrides */}
                {Object.keys(editConfig.campaign_budget_overrides || {}).length > 0 && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell>Campaign</TableCell>
                          <TableCell align="right">Min Daily</TableCell>
                          <TableCell align="right">Max Daily</TableCell>
                          <TableCell align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(editConfig.campaign_budget_overrides || {}).map(([campId, bounds]) => {
                          const camp = clientCampaigns.find((c) => c.id === campId);
                          return (
                            <TableRow key={campId}>
                              <TableCell>
                                <Typography variant="body2">
                                  {camp?.name || campId}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <InlineNumberCell
                                  value={bounds.min}
                                  prefix="$"
                                  min={0}
                                  zeroIsEmpty
                                  placeholder="Global"
                                  width={104}
                                  onCommit={(n) => {
                                    const updated = { ...(editConfig.campaign_budget_overrides || {}) };
                                    const next = { ...(updated[campId] || {}) };
                                    if (n == null) delete next.min; else next.min = n;
                                    updated[campId] = next;
                                    set('campaign_budget_overrides', updated);
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <InlineNumberCell
                                  value={bounds.max}
                                  prefix="$"
                                  min={0}
                                  zeroIsEmpty
                                  placeholder="Global"
                                  width={104}
                                  onCommit={(n) => {
                                    const updated = { ...(editConfig.campaign_budget_overrides || {}) };
                                    const next = { ...(updated[campId] || {}) };
                                    if (n == null) delete next.max; else next.max = n;
                                    updated[campId] = next;
                                    set('campaign_budget_overrides', updated);
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    const updated = { ...(editConfig.campaign_budget_overrides || {}) };
                                    delete updated[campId];
                                    set('campaign_budget_overrides', updated);
                                  }}
                                >
                                  Remove
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Add new override */}
                <Autocomplete
                  options={clientCampaigns.filter((c) => !(editConfig.campaign_budget_overrides || {})[c.id])}
                  getOptionLabel={(opt) => opt.name || opt.id}
                  value={clientCampaigns.find((c) => c.id === overrideCampaignId) || null}
                  onChange={(_, val) => setOverrideCampaignId(val?.id || '')}
                  loading={campaignsLoading}
                  sx={{ width: '100%' }}
                  componentsProps={{ popper: { style: { width: 'fit-content', maxWidth: '80vw' } } }}
                  renderInput={(params) => <TextField {...params} label="Select Campaign" size="small" />}
                />
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="Min Daily Budget ($)"
                    value={overrideMin}
                    onChange={(e) => setOverrideMin(e.target.value)}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Max Daily Budget ($)"
                    value={overrideMax}
                    onChange={(e) => setOverrideMax(e.target.value)}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="outlined"
                    disabled={!overrideCampaignId}
                    onClick={() => {
                      if (!overrideCampaignId) return;
                      const bounds = {};
                      if (overrideMin) bounds.min = Number(overrideMin);
                      if (overrideMax) bounds.max = Number(overrideMax);
                      set('campaign_budget_overrides', {
                        ...(editConfig.campaign_budget_overrides || {}),
                        [overrideCampaignId]: bounds,
                      });
                      setOverrideCampaignId('');
                      setOverrideMin('');
                      setOverrideMax('');
                    }}
                  >
                    Add Override
                  </Button>
                </Stack>

                <Divider />
                <Typography variant="subtitle2">Territory Bid Overrides</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Manual ± adjustment added to every scored city in the chosen territory before bucket-snapping. Range -90 to +90.
                  Pick a vertical to scope the override; choose <em>All Verticals</em> to apply to every vertical at once. A vertical-specific row wins over an All-Verticals row for the same territory.
                  <br />
                  <strong>Backfill unscored cities</strong> (bid-down only): when on, also adds the biggest unscored cities in the territory into the bid-down buckets — biggest cities go to the deepest allowed bucket. Useful when a territory is overpacing because Facebook is spending on cities that haven’t hit the optimizer’s scoring threshold.
                </Typography>

                {clientPacingHolds.length > 0 && (
                  <Alert severity="info" variant="outlined" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                    <strong>Active client pacing adjustments (auto-layered)</strong>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      Submitted via Pacing &amp; Adjustments. The optimizer stacks these onto the manual overrides
                      below at run time — they never appear in the saved config. Cancel them from the Pacing page.
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                      {clientPacingHolds.map((h) => {
                        const approxPct = { boost: '≈ +30%', slow_down: '≈ −35%', minimal_spend: '≈ −80%' }[h.tier] || '';
                        return (
                          <Chip
                            key={h.id}
                            size="small"
                            variant="outlined"
                            color={h.tier === 'boost' ? 'success' : 'warning'}
                            label={`${h.scope_label || `Territory ${h.scope_id}`}: ${h.tier_label || h.tier} ${approxPct} (thru ${h.end_date})`}
                          />
                        );
                      })}
                    </Stack>
                  </Alert>
                )}

                {(() => {
                  // Parse a stored override key into {tid, vid} where vid=null means All Verticals.
                  // Accepts both new ("617:1" / "617:all") and legacy ("617") shapes.
                  const parseKey = (key) => {
                    const k = String(key);
                    if (k.includes(':')) {
                      const [t, v] = k.split(':', 2);
                      const vid = v === 'all' ? null : Number(v);
                      return { tid: Number(t), vid: Number.isFinite(vid) ? vid : null };
                    }
                    return { tid: Number(k), vid: null };
                  };
                  const makeKey = (tid, vid) => `${tid}:${vid == null ? 'all' : vid}`;
                  const verticalName = (vid) => {
                    if (vid == null) return 'All Verticals';
                    const v = clientVerticals.find((x) => Number(x.id) === Number(vid));
                    return v?.vertical || v?.name || `Vertical ${vid}`;
                  };

                  const entries = Object.entries(editConfig.territory_bid_overrides || {});
                  const usedScopes = new Set(entries.map(([k]) => {
                    const { tid, vid } = parseKey(k);
                    return makeKey(tid, vid);
                  }));
                  const verticalOptions = [
                    { id: 'all', label: 'All Verticals' },
                    ...clientVerticals.map((v) => ({ id: String(v.id), label: v.vertical || v.name || `Vertical ${v.id}` })),
                  ];

                  return (
                    <>
                      {entries.length > 0 && (
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Territory</TableCell>
                                <TableCell>Vertical</TableCell>
                                <TableCell align="right">Adjustment</TableCell>
                                <TableCell align="center">Backfill unscored</TableCell>
                                <TableCell>Expires</TableCell>
                                <TableCell>Set</TableCell>
                                <TableCell align="right" sx={{ width: 80 }} />
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {entries.map(([rawKey, raw]) => {
                                const { tid, vid } = parseKey(rawKey);
                                const isObj = typeof raw === 'object' && raw !== null;
                                const pct = isObj ? Number(raw.pct) : Number(raw);
                                const backfill = isObj ? !!raw.backfill : false;
                                const expires = isObj ? (raw.expires || '') : '';
                                const setBy = isObj ? (raw.set_by || '') : '';
                                const setAt = isObj ? (raw.set_at || '') : '';
                                const meta = isObj ? raw : {};
                                const isExpired = expires && expires < new Date().toISOString().slice(0, 10);
                                const t = clientTerritories.find((ct) => Number(ct.id) === tid);
                                const normalizedKey = makeKey(tid, vid);
                                return (
                                  <TableRow key={rawKey}>
                                    <TableCell>
                                      <Typography variant="body2">{t?.name || `Territory ${tid}`}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={verticalName(vid)}
                                        size="small"
                                        variant={vid == null ? 'filled' : 'outlined'}
                                        color={vid == null ? 'default' : 'primary'}
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      <InlineNumberCell
                                        value={pct}
                                        suffix="%"
                                        min={-90}
                                        max={90}
                                        rejectZero
                                        width={92}
                                        textColor={pct > 0 ? 'success.main' : 'error.main'}
                                        onCommit={(n) => {
                                          if (n == null) return; // empty → leave as-is (use Remove to delete)
                                          const updated = { ...(editConfig.territory_bid_overrides || {}) };
                                          // Drop any legacy-shape entry, write the normalized one.
                                          // Backfill only applies to bid-downs, so clear it when going non-negative.
                                          delete updated[rawKey];
                                          updated[normalizedKey] = { ...meta, pct: n, backfill: n < 0 ? backfill : false };
                                          set('territory_bid_overrides', updated);
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      <Tooltip title={pct >= 0 ? 'Backfill only applies to bid-downs (negative %)' : 'Toggle to include unscored cities'}>
                                        <span>
                                          <Checkbox
                                            size="small"
                                            checked={backfill && pct < 0}
                                            disabled={pct >= 0}
                                            onChange={(e) => {
                                              const updated = { ...(editConfig.territory_bid_overrides || {}) };
                                              // Drop any legacy-shape entry, write the normalized one
                                              delete updated[rawKey];
                                              updated[normalizedKey] = { ...meta, pct, backfill: e.target.checked };
                                              set('territory_bid_overrides', updated);
                                            }}
                                          />
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        type="date"
                                        size="small"
                                        value={expires}
                                        error={Boolean(isExpired)}
                                        helperText={isExpired ? 'expired — sweep will clear' : ''}
                                        onChange={(e) => {
                                          const updated = { ...(editConfig.territory_bid_overrides || {}) };
                                          delete updated[rawKey];
                                          const next = { ...meta, pct, backfill };
                                          if (e.target.value) next.expires = e.target.value; else delete next.expires;
                                          updated[normalizedKey] = next;
                                          set('territory_bid_overrides', updated);
                                        }}
                                        sx={{ width: 150 }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="caption" color="text.secondary">
                                        {setAt || '—'}{setBy ? ` · ${setBy}` : ''}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Button
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                          const updated = { ...(editConfig.territory_bid_overrides || {}) };
                                          delete updated[rawKey];
                                          set('territory_bid_overrides', updated);
                                        }}
                                      >
                                        Remove
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}

                      <Stack direction="row" spacing={2} alignItems="center">
                        <Autocomplete
                          options={clientTerritories.filter((ct) => {
                            // Only filter out a territory if BOTH "all-verticals" AND the selected vertical
                            // already exist — operator can still add a vertical-specific override even if
                            // an all-verticals one exists, and vice versa.
                            const candidateKey = makeKey(Number(ct.id), territoryOverrideVerticalId === 'all' ? null : Number(territoryOverrideVerticalId));
                            return !usedScopes.has(candidateKey);
                          })}
                          getOptionLabel={(opt) => opt.name || `Territory ${opt.id}`}
                          value={clientTerritories.find((ct) => String(ct.id) === territoryOverrideId) || null}
                          onChange={(_, val) => setTerritoryOverrideId(val ? String(val.id) : '')}
                          loading={territoriesLoading}
                          sx={{ flex: 2 }}
                          renderInput={(params) => <TextField {...params} label="Select Territory" size="small" />}
                        />
                        <FormControl size="small" sx={{ flex: 1.4 }}>
                          <InputLabel>Vertical</InputLabel>
                          <Select
                            label="Vertical"
                            value={territoryOverrideVerticalId || 'all'}
                            onChange={(e) => setTerritoryOverrideVerticalId(e.target.value)}
                          >
                            {verticalOptions.map((opt) => (
                              <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label="Adjustment (%)"
                          value={territoryOverridePct}
                          onChange={(e) => setTerritoryOverridePct(e.target.value)}
                          size="small"
                          placeholder="-40"
                          sx={{ flex: 1 }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={territoryOverrideBackfill}
                              onChange={(e) => setTerritoryOverrideBackfill(e.target.checked)}
                              disabled={Number(territoryOverridePct) >= 0}
                            />
                          }
                          label={<Typography variant="caption">Backfill unscored</Typography>}
                          sx={{ ml: 0, mr: 0 }}
                        />
                        <TextField
                          type="date"
                          label="Expires"
                          size="small"
                          value={territoryOverrideExpires}
                          onChange={(e) => setTerritoryOverrideExpires(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: 160 }}
                        />
                        <Button
                          variant="outlined"
                          disabled={!territoryOverrideId || territoryOverridePct === ''}
                          onClick={() => {
                            const pct = Number(territoryOverridePct);
                            if (!Number.isFinite(pct) || pct === 0) return;
                            const clamped = Math.max(-90, Math.min(90, Math.round(pct)));
                            const backfill = clamped < 0 && territoryOverrideBackfill;
                            const vid = territoryOverrideVerticalId === 'all' ? null : Number(territoryOverrideVerticalId);
                            const key = makeKey(Number(territoryOverrideId), vid);
                            const entry = { pct: clamped, backfill };
                            if (territoryOverrideExpires) entry.expires = territoryOverrideExpires;
                            set('territory_bid_overrides', {
                              ...(editConfig.territory_bid_overrides || {}),
                              [key]: entry,
                            });
                            setTerritoryOverrideId('');
                            setTerritoryOverridePct('');
                            setTerritoryOverrideBackfill(false);
                            setTerritoryOverrideVerticalId('all');
                            setTerritoryOverrideExpires('');
                          }}
                        >
                          Add Override
                        </Button>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Overrides are month-scoped: without an explicit date they expire at the END OF THE
                        CURRENT MONTH, the optimizer ignores them once lapsed, and the daily sweep clears
                        them with an email digest. While an override is active, the automatic pacing
                        correction for that territory stands down (manual wins — no stacking).
                      </Typography>
                    </>
                  );
                })()}

                <Divider />
                <Typography variant="subtitle2">City Bid Buckets</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Adjustment % each city snaps to. Range -90 to +90, max 9 buckets (Facebook caps rule sets at 10; 1 slot reserved for the age rule). Default: -80, -60, -40, -20, 15, 25, 40 (Controller v2 adds -30). Leaving the stock default here means the client inherits future default changes; any edit makes the ladder custom.
                </Typography>
                <CityBidBucketsEditor
                  value={editConfig.city_bid_buckets || []}
                  onChange={(next) => set('city_bid_buckets', next)}
                />

              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !editConfig}>
            {saving ? <CircularProgress size={20} /> : editConfig?.id ? 'Save Changes' : 'Create Configuration'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Data Health Tab ─────────────────────────────────────────────

const HEALTH_COLORS = { green: 'success', yellow: 'warning', red: 'error' };
const HEALTH_LABELS = { green: 'Green', yellow: 'Yellow', red: 'Red' };

function HealthChip({ status }) {
  return (
    <Chip
      label={HEALTH_LABELS[status] || status || '—'}
      color={HEALTH_COLORS[status] || 'default'}
      size="small"
    />
  );
}

function ConsistencyTable({ title, data, layerLabels }) {
  if (!data || Object.keys(data).length === 0) return null;
  const status = data.status;
  const variancePct = data.variance_pct;
  const layerKeys = Object.keys(layerLabels);
  // Multi-window: render one row per window (30d/60d/90d) so the optimizer's
  // lookback windows are individually visible. Falls back to the legacy single-row
  // shape for old audit rows that don't have a `windows` map.
  const windows = data.windows || null;
  const windowEntries = windows ? Object.entries(windows) : [['snapshot', data]];

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">{title}</Typography>
        <HealthChip status={status} />
        {variancePct != null && (
          <Typography variant="caption" color="text.secondary">
            worst variance {Number(variancePct).toFixed(1)}%
          </Typography>
        )}
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80 }}>Window</TableCell>
              {layerKeys.map((k) => (
                <TableCell key={k} align="right">{layerLabels[k]}</TableCell>
              ))}
              <TableCell align="right" sx={{ width: 100 }}>Variance</TableCell>
              <TableCell align="center" sx={{ width: 80 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {windowEntries.map(([label, payload]) => (
              <TableRow key={label}>
                <TableCell sx={{ fontWeight: 500 }}>{label}</TableCell>
                {layerKeys.map((k) => (
                  <TableCell key={k} align="right">
                    {payload[k] != null ? Number(payload[k]).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
                  </TableCell>
                ))}
                <TableCell align="right">
                  {payload.variance_pct != null ? `${Number(payload.variance_pct).toFixed(1)}%` : '—'}
                </TableCell>
                <TableCell align="center">
                  <HealthChip status={payload.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

const SPEND_LAYERS = {
  client_summary: 'Client Summary',
  daily_pulse: 'Daily Pulse',
  dma_total: 'DMA Total',
  state_total: 'State Total',
};
const LEAD_LAYERS = {
  client_summary: 'Client Summary',
  daily_pulse: 'Daily Pulse',
  city_total: 'City',
  county_total: 'County',
  dma_total: 'DMA',
  state_total: 'State',
};

function AuditDetail({ audit }) {
  const backend = audit.backend_consistency || {};
  const backendMetrics = Object.keys(backend);
  const freshness = audit.freshness || {};
  const suspicious = audit.suspicious_values || [];

  return (
    <Box sx={{ p: 2, bgcolor: 'background.default' }}>
      <ConsistencyTable title="Spend Consistency" data={audit.spend_consistency} layerLabels={SPEND_LAYERS} />
      <ConsistencyTable title="Lead Consistency" data={audit.lead_consistency} layerLabels={LEAD_LAYERS} />

      {backendMetrics.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Backend Funnel Consistency</Typography>
          {backendMetrics.map((m) => (
            <ConsistencyTable
              key={m}
              title={m.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              data={backend[m]}
              layerLabels={LEAD_LAYERS}
            />
          ))}
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Freshness</Typography>
          <HealthChip status={freshness.status} />
          <Typography variant="caption" color="text.secondary">
            stalest layer: {freshness.max_days_stale ?? '—'}d
          </Typography>
        </Stack>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Layer</TableCell>
                <TableCell>Latest Date</TableCell>
                <TableCell align="right">Days Stale</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(freshness.latest_dates || {}).map(([layer, date]) => (
                <TableRow key={layer}>
                  <TableCell>{layer}</TableCell>
                  <TableCell>{date || '—'}</TableCell>
                  <TableCell align="right">{(freshness.days_stale || {})[layer] ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Suspicious Values {suspicious.length > 0 && `(${suspicious.length})`}
        </Typography>
        {suspicious.length === 0 ? (
          <Typography variant="body2" color="text.secondary">None flagged.</Typography>
        ) : (
          <Stack spacing={0.5}>
            {suspicious.map((s, i) => (
              <Alert key={i} severity={s.type === 'com_extreme' ? 'error' : 'warning'} variant="outlined">
                {s.msg}
              </Alert>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

function DataHealthTab({ clients }) {
  const [latest, setLatest] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [running, setRunning] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    fetchGeoAuditsLatest()
      .then((res) => setLatest(res.data || []))
      .catch((err) => setError(err?.message || 'Failed to load audits.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAudit = useCallback((clientId) => {
    setRunning(clientId || 'all');
    triggerGeoAudit(clientId)
      .then(() => load())
      .catch((err) => setError(err?.message || 'Audit failed.'))
      .finally(() => setRunning(null));
  }, [load]);

  const counts = useMemo(() => {
    const c = { red: 0, yellow: 0, green: 0 };
    latest.forEach((a) => { if (c[a.overall_status] != null) c[a.overall_status] += 1; });
    return c;
  }, [latest]);

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2}>
          <StatCard label="Red" value={counts.red} color="error.main" />
          <StatCard label="Yellow" value={counts.yellow} color="warning.main" />
          <StatCard label="Green" value={counts.green} color="success.main" />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            onClick={() => runAudit(null)}
            disabled={running != null}
          >
            {running === 'all' ? <CircularProgress size={20} /> : 'Run Audit (all clients)'}
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {loading && latest.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : latest.length === 0 ? (
        <Alert severity="info">
          No audits yet. Click "Run Audit" to generate the first round.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Client</TableCell>
                <TableCell>Audit Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {latest.map((a) => {
                const isOpen = !!expanded[a.id];
                return (
                  <Fragment key={a.id}>
                    <TableRow hover sx={{ '& > *': { borderBottom: isOpen ? 'none' : undefined } }}>
                      <TableCell sx={{ p: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => setExpanded((e) => ({ ...e, [a.id]: !e[a.id] }))}
                        >
                          {isOpen ? <CollapseIcon /> : <ExpandIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{a.client_name}</TableCell>
                      <TableCell>{fmtDate(a.audit_date)}</TableCell>
                      <TableCell><HealthChip status={a.overall_status} /></TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {a.notes || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={() => runAudit(a.client)}
                          disabled={running != null}
                        >
                          {running === a.client ? <CircularProgress size={16} /> : 'Re-audit'}
                        </Button>
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ p: 0 }}>
                          <AuditDetail audit={a} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}

// ── Main Page ───────────────────────────────────────────────────

// Deep-link tab names → index (Performance tab retired). Keeps URLs like
// /facebook/budget-optimizer?tab=settings&client_id=42 readable.
const TAB_INDEX = { runs: 0, 'data-health': 1, settings: 2 };

function BudgetOptimizerPage() {
  const [searchParams] = useSearchParams();
  const deepLinkClientId = searchParams.get('client_id') || '';
  // Deep-link to a specific run (e.g. from a Data Health optimizer alert):
  // /facebook/budget-optimizer?client_id=42&tab=runs&run=4554 opens that run.
  const deepLinkRunId = searchParams.get('run') || '';
  const [activeTab, setActiveTab] = useState(() => TAB_INDEX[searchParams.get('tab')] ?? 0);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);

  useEffect(() => {
    fetchClients()
      .then((res) => setClients(res.data || []))
      .catch(() => {})
      .finally(() => setClientsLoading(false));
  }, []);

  return (
    <Box>
      <Stack spacing={3}>
        <Card>
          <CardHeader
            title="Budget Optimizer"
            subheader="Automated campaign budget pacing and city-level bid optimization"
          />
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Optimizer Runs" />
            <Tab label="Data Health" />
            <Tab label="Settings" />
          </Tabs>
        </Card>

        {clientsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            {activeTab === 0 && <OptimizerRunsTab clients={clients} initialClientId={deepLinkClientId} initialRunId={deepLinkRunId} />}
            {activeTab === 1 && <DataHealthTab clients={clients} />}
            {activeTab === 2 && <OptimizerSettingsTab clients={clients} initialClientId={deepLinkClientId} />}
          </>
        )}
      </Stack>
    </Box>
  );
}

export default BudgetOptimizerPage;
